import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { ArrowRight, Check, Quote, Star, ArrowUpRight, ArrowDown,
  Zap, Shield, Users, BarChart3, BarChart3 as BarChart, Globe, Palette, HeadphonesIcon, TrendingUp,
  Brain, Rocket, ShieldCheck, Linkedin, ExternalLink, Code2, Radio, Cloud, Compass,
  Server, Cpu, Layers, Lock, Sparkles, Terminal, CreditCard, ScanFace, Video, Briefcase, Network, Database,
  X, Smartphone, Phone, AlertTriangle, FileText, CheckCircle2, ChevronDown, Award, Target, Eye, Lightbulb, Blocks, Telescope, BrainCircuit } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Reveal, CountUp, Magnetic, WordReveal, TypewriterText } from "./motion";
import { BrowserMock, PhoneMock, FloatingStat, AbstractOrb, Spotlight } from "./Visuals";
import { Draggable3DPhotoRing } from "./Draggable3DPhotoRing";
import { openConsultationModal } from "./ConsultationModal";
import api from "../lib/api";

import {
  STATS, SERVICES, INDUSTRIES, SOLUTIONS, PROJECTS, PROCESS, TECHNOLOGIES, WHY,
  TESTIMONIALS, TEAM, BLOGS, FAQS, CLIENTS, SECTOR_CASE_STUDIES, SECTOR_LABELS,
  ALL_OFFERINGS, COMPANY_PURPOSE, COMPANY_VISION, COMPANY_MISSION, COMPANY_CORE_THOUGHT, TAGLINE,
} from "../data";

export const SectionHead = ({ eyebrow, title, sub, id, center = false, dark = false, className = "", subMotion = false }) => (
  <div id={id} className={`max-w-3xl ${className || "mb-6 sm:mb-8"} scroll-mt-28 ${center ? "mx-auto text-center" : ""}`}>
    {eyebrow && (
      <Reveal><span className="inline-flex items-center gap-2 text-[#FF5A1F] font-heading text-sm font-semibold uppercase tracking-[0.22em] mb-3 sm:mb-4">{eyebrow}</span></Reveal>
    )}
    <Reveal delay={0.1}><h2 className={`font-display text-4xl sm:text-5xl lg:text-[3.4rem] font-bold tracking-tight leading-[1.05] text-balance ${dark ? "text-white" : "text-[#0B0B0B]"}`}>{title}</h2></Reveal>
    {sub && (
      subMotion ? (
        <p className={`mt-3.5 sm:mt-4 text-base sm:text-lg leading-relaxed ${dark ? "text-neutral-400" : "text-neutral-500"}`}>{sub}</p>
      ) : (
        <Reveal delay={0.2}><p className={`mt-3.5 sm:mt-4 text-base sm:text-lg leading-relaxed ${dark ? "text-neutral-400" : "text-neutral-500"}`}>{sub}</p></Reveal>
      )
    )}
  </div>
);

/* Clients — glass marquee */
const CLIENT_LOGO_FALLBACKS = {
  "php2shell": "/brand/php2shell-logo.png",
  "wordpress-security-hardening": "/brand/wordpress-security-logo.png",
  "wordpress": "/brand/wordpress-security-logo.png",
  "paineasy": "/brand/paineasy-logo.svg",
  "medylife": "/brand/medylife-logo.png",
  "meadylife": "/brand/medylife-logo.png",
  "launchpad": "/brand/launchpad-clean.png",
  "indusdeal": "/brand/indusdeal-icon.png",
  "clueblu": "/brand/clueblu-logo.png",
  "ecopro": "/brand/ecopro-logo.png",
  "ecopro builders": "/brand/ecopro-logo.png",
  "ecoprobuilders": "/brand/ecopro-logo.png",
  "buildhealer": "/brand/buildhealer-icon.png",
  "krishna saree": "/brand/krishnasaree-logo.png",
  "krishnasaree": "/brand/krishnasaree-logo.png",
  "pawsup": "/brand/pawsup-icon.png",
  "pawsup uae": "/brand/pawsup-icon.png",
  "foxrun": "/brand/foxrun-logo.png",
  "ciaz travels": "/brand/ciaztravels-logo.png",
  "ciaztravels": "/brand/ciaztravels-logo.png",
  "vinnskolam": "/brand/vinnskolam-logo.png",
  "vinns": "/brand/vinnskolam-logo.png",
  "kolam": "/brand/vinnskolam-logo.png",
  "latajcollection": "/brand/latajcollection-logo.png",
  "la taj collection": "/brand/latajcollection-logo.png",
  "lataj": "/brand/latajcollection-logo.png",
  "kolams": "/brand/vinnskolam-logo.png",
  "kolams of india": "/brand/vinnskolam-logo.png",
  "lockiva": "/brand/lockiva-logo.png",
  "aqua 5 plus": "/brand/aqua5plus-clean.png",
  "aqua5plus": "/brand/aqua5plus-clean.png",
  "aqua": "/brand/aqua5plus-clean.png",
};

const EXCLUDED_BRANDS = ["dewstream", "aqua"];
const filterBrandLogos = (list) =>
  (list || []).filter((c) => {
    const name = (c.name || "").toLowerCase().trim();
    const domain = (c.domain || "").toLowerCase().trim();
    return !EXCLUDED_BRANDS.some((ex) => name.includes(ex) || domain.includes(ex));
  });

export const TrustedBy = () => {
  const [clientList, setClientList] = useState(() => filterBrandLogos(CLIENTS));

  useEffect(() => {
    api.get("/clients")
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          const activeClients = filterBrandLogos(CLIENTS);
          const updated = activeClients.map((base) => {
            const found = res.data.find(
              (d) =>
                d.name?.toLowerCase().trim() === base.name.toLowerCase().trim() ||
                d.domain?.toLowerCase().trim() === base.domain.toLowerCase().trim()
            );
            return found ? { ...base, ...found, logo: base.logo || found.logo } : base;
          });
          setClientList(updated);
        }
      })
      .catch(() => {});
  }, []);

  const renderCard = (c, keyId) => {
    if (!c) return null;
    const key = (c.name || "").toLowerCase().trim();
    const domainKey = (c.domain || "").toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, "").replace(/\.[a-z.]+$/, "");
    if (EXCLUDED_BRANDS.some((ex) => key.includes(ex) || domainKey.includes(ex))) {
      return null;
    }
    const logoUrl = CLIENT_LOGO_FALLBACKS[key] || CLIENT_LOGO_FALLBACKS[domainKey] || c.logo || `https://www.google.com/s2/favicons?domain=${c.domain}&sz=128`;

    const projectMatch = PROJECTS.find(
      (p) =>
        p.title.toLowerCase() === c.name.toLowerCase() ||
        (p.domain && c.domain && p.domain.toLowerCase() === c.domain.toLowerCase()) ||
        p.slug === c.name.toLowerCase().replace(/\s+/g, "-") ||
        (c.domain && p.slug === c.domain.replace(/\.[a-z]+$/, ""))
    );

    const handleImgError = (e) => {
      e.target.onerror = null;
      const fallback = CLIENT_LOGO_FALLBACKS[key] || CLIENT_LOGO_FALLBACKS[domainKey] || projectMatch?.logo;
      if (fallback && !e.target.src.endsWith(fallback)) {
        e.target.src = fallback;
      }
    };

    if (projectMatch) {
      return (
        <Link
          key={keyId}
          to={`/portfolio/${projectMatch.slug}`}
          className="flex items-center justify-center shrink-0 px-3 sm:px-5 hover:scale-105 transition-all duration-300 opacity-90 hover:opacity-100 group"
          title={`Explore ${c.name} Case Study`}
        >
          <img
            src={logoUrl}
            alt={c.name}
            className="h-10 sm:h-11 md:h-12 w-auto max-w-[170px] sm:max-w-[200px] object-contain shrink-0"
            onError={handleImgError}
          />
        </Link>
      );
    }

    return (
      <a
        key={keyId}
        href={`https://${c.domain}`}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="flex items-center justify-center shrink-0 px-3 sm:px-5 hover:scale-105 transition-all duration-300 opacity-90 hover:opacity-100"
        title={c.name}
      >
        <img
          src={logoUrl}
          alt={c.name}
          className="h-10 sm:h-11 md:h-12 w-auto max-w-[170px] sm:max-w-[200px] object-contain shrink-0"
          onError={handleImgError}
        />
      </a>
    );
  };

  return (
    <section className="py-5 sm:py-6 md:py-7 mesh-light overflow-hidden border-y border-black/[0.06]">
      <p className="text-center text-[11px] sm:text-xs uppercase tracking-[0.25em] text-neutral-400 mb-3.5 sm:mb-4 font-semibold">
        Trusted by ambitious teams worldwide
      </p>
      <div
        className="flex overflow-hidden w-full select-none marquee-track-container relative"
        style={{
          maskImage: "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
          WebkitMaskImage: "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
        }}
      >
        <div className="flex gap-12 sm:gap-16 md:gap-20 items-center shrink-0 pr-12 sm:pr-16 md:pr-20 animate-marquee-continuous">
          {clientList.map((c, i) => renderCard(c, `track1-a-${i}`))}
          {clientList.map((c, i) => renderCard(c, `track1-b-${i}`))}
          {clientList.map((c, i) => renderCard(c, `track1-c-${i}`))}
        </div>
        <div className="flex gap-12 sm:gap-16 md:gap-20 items-center shrink-0 pr-12 sm:pr-16 md:pr-20 animate-marquee-continuous" aria-hidden="true">
          {clientList.map((c, i) => renderCard(c, `track2-a-${i}`))}
          {clientList.map((c, i) => renderCard(c, `track2-b-${i}`))}
          {clientList.map((c, i) => renderCard(c, `track2-c-${i}`))}
        </div>
      </div>
    </section>
  );
};



/* Who we are — sleek dark layout with luminous earth network map on black background */
export const AboutIntro = () => (
  <section className="relative py-12 sm:py-16 lg:py-20 px-5 sm:px-8 bg-[#0B0B0C] border-y border-white/[0.08] overflow-hidden" id="about-intro">
    {/* Global Earth Network Map on Deep Black Background */}
    <div className="absolute inset-0 pointer-events-none select-none overflow-hidden z-0 flex items-center justify-center">
      {/* Soft warm radial glow behind the earth globes */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(circle at 68% 50%, rgba(255, 90, 31, 0.14) 0%, rgba(255, 90, 31, 0.03) 45%, transparent 70%)",
        }}
      />
      <img
        src="/brand/who-we-are-dark-bg.png"
        alt="Global interconnected ecosystem"
        className="w-full h-full object-cover object-center pointer-events-none select-none opacity-90"
        loading="eager"
      />
      {/* Deep black gradient mask on left side to ensure 100% crystal-clear text readability */}
      <div className="absolute inset-y-0 left-0 w-full md:w-3/4 lg:w-3/5 bg-gradient-to-r from-[#0B0B0C] via-[#0B0B0C]/90 to-transparent pointer-events-none" />
      {/* Subtle edge fade to blend seamlessly with surrounding sections */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0C]/70 via-transparent to-[#0B0B0C]/70 pointer-events-none" />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto">
      <div className="max-w-2xl lg:max-w-3xl">
        <span className="inline-flex items-center text-[#FF5A1F] font-heading text-xs sm:text-sm font-bold uppercase tracking-[0.24em] mb-3">
          WHO ARE WE
        </span>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.85rem] font-bold tracking-tight leading-[1.15] text-white mb-5">
          A Team Dedicated to Make Your Business <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF5A1F] via-[#FF7A30] to-[#FFA040]">Ready for Tomorrow</span>
        </h2>

        <Reveal delay={0.1}>
          <div className="space-y-4">
            <p className="text-base sm:text-lg text-neutral-300 leading-relaxed font-normal">
              We are a team, here to help businesses be ready for the changing times. From developing applications and websites to help manage them, NeuraVolt is on a venture to bring fragmented technology together under one, interconnected ecosystem.
            </p>
            <p className="text-base sm:text-lg text-neutral-300 leading-relaxed font-normal">
              We dream BIG. And we work to achieve it so that, business of all sizes can focus on growth and not about managing operations.
            </p>
          </div>
        </Reveal>

        {/* Core Pillars / Capabilities Tags */}
        <Reveal delay={0.2}>
          <div className="mt-6 flex flex-wrap gap-2.5">
            {["AI Solutions", "Cloud Infrastructure", "Managed Services", "24×7 Support"].map((pill) => (
              <span
                key={pill}
                className="px-4 py-2 rounded-full text-xs sm:text-sm font-semibold tracking-wide bg-white/[0.06] text-neutral-200 border border-white/10 hover:border-[#FF5A1F]/50 hover:text-white hover:bg-[#FF5A1F]/10 shadow-[0_4px_12px_rgba(0,0,0,0.4)] backdrop-blur-md transition-all duration-300"
              >
                {pill}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.3}>
          <Link to="/about" className="mt-7 inline-flex items-center gap-2 text-[#FF5A1F] font-bold text-sm sm:text-base group hover:text-[#FFA040] transition-colors">
            Know More About Us <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);


/* ───────────────────────────────────────────────────────────── */
/* 3D Animated Heroic Core Thought Card matching Image 2         */
/* ───────────────────────────────────────────────────────────── */
export const HeroicCoreThoughtCard = ({ showExploreButton = true }) => {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: -(y * 6), y: x * 6 });
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
    setIsHovered(false);
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      style={{ perspective: "1200px" }}
      className="relative w-full select-none"
    >
      {/* Continuous Whole-Card Floating Motion with Interactive 3D Perspective Tilt */}
      <motion.div
        animate={{
          y: [-7, 7, -7],
          rotateZ: [-0.3, 0.3, -0.3],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        style={{
          transform: `perspective(1200px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transition: isHovered ? "transform 0.15s ease-out" : "transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
          transformStyle: "preserve-3d",
        }}
        className="relative overflow-hidden rounded-[1.75rem] sm:rounded-[2.25rem] border border-[#FF5A1F]/35 p-6 sm:p-8 lg:p-10 bg-[#070709] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9),0_0_35px_-5px_rgba(255,90,31,0.22)] transition-shadow duration-500 hover:border-[#FF5A1F]/60 hover:shadow-[0_30px_70px_-15px_rgba(0,0,0,0.95),0_0_45px_rgba(255,90,31,0.3)]"
      >
        {/* Dynamic Cursor Spotlight Reflection */}
        {isHovered && (
          <div
            className="pointer-events-none absolute inset-0 z-20 transition-opacity duration-300"
            style={{
              background: `radial-gradient(600px circle at ${mousePos.x}px ${mousePos.y}px, rgba(255,90,31,0.12), transparent 70%)`,
            }}
          />
        )}

        {/* 3D Rendered Metallic Curved Vortex Ribbon Graphic (Right Side) */}
        <div className="absolute right-0 top-0 bottom-0 w-[52%] sm:w-[46%] lg:w-[42%] xl:w-[39%] pointer-events-none select-none z-10 overflow-hidden flex items-center justify-end">
          {/* Volumetric Ambient Orange Backlight Glow on Right */}
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.3, 0.52, 0.3],
            }}
            transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-10 -right-10 w-[440px] h-[440px] rounded-full bg-gradient-to-br from-[#FF5A1F]/30 via-[#FF8A3D]/15 to-transparent blur-[100px]"
          />

          {/* 3D graphic image with smooth fade on left */}
          <motion.div
            animate={{
              y: [-3, 3, -3],
              rotate: [-0.25, 0.25, -0.25],
            }}
            transition={{
              duration: 6.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="w-full h-full flex items-center justify-end"
            style={{
              maskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.85) 15%, black 35%)",
              WebkitMaskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.85) 15%, black 35%)",
            }}
          >
            <img
              src="/brand/core-thought-3d.png?v=12"
              alt="NeuraVolt 3D Intelligent Architecture"
              className="h-full w-full object-cover object-right select-none pointer-events-none"
              loading="eager"
            />
          </motion.div>
        </div>

        {/* Foreground Content */}
        <div className="relative z-20 w-full">
          {/* Quote Typography with Highlight */}
          <blockquote className="font-display text-xl sm:text-2xl lg:text-[1.85rem] font-bold text-white leading-[1.3] tracking-tight mb-5 sm:mb-6 max-w-xl lg:max-w-[58%]">
            <span className="text-[#FF5A1F] text-2xl sm:text-3xl font-serif select-none mr-2">“</span>
            Technology is fragmented.{" "}
            <span className="text-[#FF5A1F]">NeuraVolt brings it together</span>{" "}
            into one integrated, evolving ecosystem.”
          </blockquote>

          {/* Horizontal Subtle Divider Line */}
          <div className="w-full max-w-xl lg:max-w-[58%] h-px bg-white/10 mb-5" />

          {/* Bottom Bar: 4 Strategic Pillars & Explore Our Story CTA Button */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 w-full pt-1">
            {/* 4 Pillars with dedicated orange icons strictly on one single line */}
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4 text-xs sm:text-[13px] text-neutral-300 font-medium whitespace-nowrap overflow-x-auto no-scrollbar py-0.5">
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                <Layers className="w-4 h-4 text-[#FF5A1F] shrink-0" />
                <span>Sovereign Platforms</span>
              </div>
              <span className="text-white/20 select-none shrink-0">|</span>
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                <Cloud className="w-4 h-4 text-[#FF5A1F] shrink-0" />
                <span>Cloud DevOps</span>
              </div>
              <span className="text-white/20 select-none shrink-0">|</span>
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                <BarChart3 className="w-4 h-4 text-[#FF5A1F] shrink-0" />
                <span>AI Systems</span>
              </div>
              <span className="text-white/20 select-none shrink-0">|</span>
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                <Database className="w-4 h-4 text-[#FF5A1F] shrink-0" />
                <span>Enterprise Infrastructure</span>
              </div>
            </div>

            {/* Explore Our Story Pill Capsule Button */}
            {showExploreButton && (
              <Link
                to="/about"
                className="inline-flex items-center gap-3 pl-4 sm:pl-5 pr-1.5 py-1.5 rounded-full bg-black/80 hover:bg-black border border-white/15 hover:border-[#FF5A1F]/60 backdrop-blur-md text-white text-xs sm:text-sm font-semibold transition-all duration-300 group shadow-[0_10px_25px_rgba(0,0,0,0.6)] shrink-0 hover:shadow-[0_10px_30px_rgba(255,90,31,0.25)] hover:scale-[1.02] w-fit"
              >
                <span>Explore Our Story</span>
                <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-tr from-[#FF5A1F] to-[#FF8A3D] flex items-center justify-center text-white shadow-[0_0_14px_rgba(255,90,31,0.6)] group-hover:scale-105 group-hover:shadow-[0_0_22px_rgba(255,90,31,0.95)] transition-all">
                  <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

/* ───────────────────────────────────────────────────────────── */
/* 3 Strategic Pillars: Purpose, Vision & Mission                */
/* ───────────────────────────────────────────────────────────── */
/* ───────────────────────────────────────────────────────────── */
/* 3 Strategic Pillars: Purpose, Vision & Mission (Stack Shuffle)*/
/* ───────────────────────────────────────────────────────────── */
const STRATEGIC_PILLARS = [
  {
    id: "purpose",
    step: "01",
    label: "OUR PURPOSE",
    title: "Connect Fragmented Tech",
    description: COMPANY_PURPOSE,
    footerText: "One Connected Ecosystem",
    icon: Blocks,
    accentBg: "bg-[#FF5A1F]",
    accentColor: "#FF5A1F",
    iconColor: "text-[#FF5A1F]",
    glowColor: "rgba(255, 90, 31, 0.2)",
    tagBg: "bg-[#FF5A1F]/10 text-[#FF5A1F]",
  },
  {
    id: "vision",
    step: "02",
    label: "OUR VISION",
    title: "Global Integrated Growth",
    description: COMPANY_VISION,
    footerText: "Build • Operate • Grow",
    icon: Telescope,
    accentBg: "bg-blue-600",
    accentColor: "#2563EB",
    iconColor: "text-blue-600",
    glowColor: "rgba(37, 99, 235, 0.2)",
    tagBg: "bg-blue-500/10 text-blue-600",
  },
  {
    id: "mission",
    step: "03",
    label: "OUR MISSION",
    title: "Continuous Innovation",
    description: COMPANY_MISSION,
    footerText: "Innovate • Build • Integrate",
    icon: BrainCircuit,
    accentBg: "bg-emerald-600",
    accentColor: "#059669",
    iconColor: "text-emerald-600",
    glowColor: "rgba(5, 150, 105, 0.2)",
    tagBg: "bg-emerald-500/10 text-emerald-600",
  },
];

export const StrategicPillars = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);
  const timerRef = useRef(null);
  const isInView = useInView(containerRef, { once: false, amount: 0.2 });

  // Responsive screen check for 3D stack perspective offset
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Continuous auto-shuffle scrolling motion cycle (every 1.8s)
  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!isHovered && isInView) {
      timerRef.current = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % STRATEGIC_PILLARS.length);
      }, 1800);
    }
  }, [isHovered, isInView]);

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resetTimer]);

  const handlePrev = () => {
    setActiveIndex((prev) => (prev - 1 + STRATEGIC_PILLARS.length) % STRATEGIC_PILLARS.length);
    resetTimer();
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % STRATEGIC_PILLARS.length);
    resetTimer();
  };

  const handleSelect = (idx) => {
    setActiveIndex(idx);
    resetTimer();
  };

  // 3D Stack Shuffle motion positions based on cyclic distance
  const getCardStyle = (diff) => {
    switch (diff) {
      case 0:
        return {
          x: "0%",
          scale: 1,
          y: -6,
          zIndex: 30,
          opacity: 1,
          rotateY: 0,
          rotateZ: 0,
          filter: "blur(0px) brightness(1)",
          pointerEvents: "auto",
        };
      case 1:
        return {
          x: isMobile ? "8%" : "44%",
          scale: isMobile ? 0.92 : 0.88,
          y: isMobile ? 14 : 10,
          zIndex: 20,
          opacity: isMobile ? 0.6 : 0.68,
          rotateY: isMobile ? 0 : -8,
          rotateZ: isMobile ? 2 : 1.5,
          filter: "blur(0.3px) brightness(0.94)",
          pointerEvents: "auto",
        };
      case 2:
      default:
        return {
          x: isMobile ? "-8%" : "-44%",
          scale: isMobile ? 0.92 : 0.88,
          y: isMobile ? 14 : 10,
          zIndex: 10,
          opacity: isMobile ? 0.45 : 0.68,
          rotateY: isMobile ? 0 : 8,
          rotateZ: isMobile ? -2 : -1.5,
          filter: "blur(0.3px) brightness(0.94)",
          pointerEvents: "auto",
        };
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-6xl mx-auto py-1 select-none"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* 3D Stack Shuffle Arena */}
      <div
        className="relative w-full min-h-[250px] sm:min-h-[280px] flex items-center justify-center overflow-visible"
        style={{ perspective: "1400px" }}
      >
        {STRATEGIC_PILLARS.map((p, idx) => {
          const diff = (idx - activeIndex + STRATEGIC_PILLARS.length) % STRATEGIC_PILLARS.length;
          const isActive = diff === 0;
          const Icon = p.icon;

          return (
            <motion.div
              key={p.id}
              animate={getCardStyle(diff)}
              transition={{
                type: "spring",
                stiffness: 240,
                damping: 24,
                mass: 0.85,
              }}
              drag={isActive ? "x" : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = Math.abs(offset.x) * velocity.x;
                if (offset.x < -40 || swipe < -100) {
                  handleNext();
                } else if (offset.x > 40 || swipe > 100) {
                  handlePrev();
                }
              }}
              onClick={() => {
                if (!isActive) handleSelect(idx);
              }}
              className={`absolute w-[92%] sm:w-[480px] md:w-[530px] max-w-full rounded-2xl p-5 sm:p-6 bg-gradient-to-b from-white via-[#FCFCFD] to-[#F8F8FA] border transition-colors duration-300 select-none overflow-hidden cursor-pointer ${
                isActive
                  ? "border-black/15"
                  : "border-black/[0.08] hover:border-black/20"
              }`}
              style={{
                boxShadow: isActive
                  ? `0 28px 70px -15px rgba(0,0,0,0.15), 0 12px 28px -6px rgba(0,0,0,0.06), 0 0 45px ${p.glowColor}`
                  : "0 10px 30px -10px rgba(0,0,0,0.08)",
              }}
            >
              {/* Subtle Tech Dot Grid Watermark */}
              <div className="absolute inset-0 opacity-[0.03] group-hover:opacity-[0.06] pointer-events-none bg-[radial-gradient(#000_1px,transparent_1px)] [background-size:16px_16px] z-0" />

              {/* Ambient Accent Light Wash */}
              <div
                className="absolute top-0 right-0 w-44 h-44 rounded-full pointer-events-none blur-3xl opacity-20"
                style={{ backgroundColor: p.accentColor }}
              />

              {/* Card Header: Icon + Label */}
              <div className="relative z-10 flex items-center gap-3 mb-3.5">
                {/* Icon Box */}
                <div
                  className={`relative shrink-0 w-10 h-10 rounded-xl bg-white border border-neutral-200/90 flex items-center justify-center ${p.iconColor} shadow-2xs transition-transform duration-300 ${
                    isActive ? "scale-105 shadow-sm" : ""
                  }`}
                >
                  <Icon className="w-4.5 h-4.5" />
                </div>

                {/* Underlined Label */}
                <div className="relative inline-flex flex-col items-start">
                  <span className="text-xs sm:text-[13px] font-mono font-bold uppercase tracking-[0.16em] text-neutral-900 leading-none">
                    {p.label}
                  </span>
                  <span
                    className={`mt-1.5 h-[5px] rounded-full ${p.accentBg} transition-all duration-500 ease-out ${
                      isActive ? "w-full h-[2px]" : "w-[5px]"
                    }`}
                  />
                </div>
              </div>

              {/* Card Title */}
              <h3 className="relative z-10 font-display text-lg sm:text-xl font-bold text-neutral-900 mb-2 tracking-tight">
                {p.title}
              </h3>

              {/* Card Description */}
              <p className="relative z-10 text-neutral-600 leading-relaxed text-xs sm:text-[14px] font-normal">
                {p.description}
              </p>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

/* ───────────────────────────────────────────────────────────── */
/* Strategic Foundation: Purpose, Vision, Mission & Core Thought */
/* ───────────────────────────────────────────────────────────── */
export const StrategicFoundation = () => (
  <section className="py-6 sm:py-9 px-5 sm:px-8 bg-gradient-to-b from-[#FAF9F6] via-white to-[#FAF9F6] border-y border-black/[0.06] relative overflow-hidden" id="foundation">
    {/* Background ambient lighting */}
    <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-gradient-to-r from-orange-400/10 via-amber-300/10 to-blue-400/10 blur-[130px] rounded-full pointer-events-none" />

    <div className="max-w-7xl mx-auto relative z-10 space-y-6 sm:space-y-7">
      {/* Section Header */}
      <div className="text-center max-w-3xl mx-auto">
        <Reveal delay={0.1}>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-[#0B0B0B] tracking-tight leading-tight mb-3">
            One Connected Ecosystem. <br className="hidden sm:inline" />
            <span className="text-[#FF5A1F]">Zero Fragmentation.</span>
          </h2>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="text-base sm:text-lg text-neutral-600 font-medium">
            {TAGLINE}
          </p>
        </Reveal>
      </div>

      {/* Heroic Core Thought Card */}
      <Reveal delay={0.2}>
        <HeroicCoreThoughtCard />
      </Reveal>

      {/* 3 Pillars: Purpose, Vision & Mission */}
      <StrategicPillars />
    </div>
  </section>
);




/* Numbers — 3-column obsidian glass showcase with cursor 3D tilt */
const STAT_ICONS = [Rocket, Users, Globe];

function TiltStatCard({ children, testId, delay }) {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -8;
    const rotateY = ((x - centerX) / centerX) * 8;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
  };

  const handleMouseLeave = () => {
    const card = cardRef.current;
    if (!card) return;
    card.style.transform = "perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)";
  };

  return (
    <Reveal delay={delay}>
      <div
        ref={cardRef}
        data-testid={testId}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="group relative rounded-3xl p-7 sm:p-9 bg-gradient-to-b from-white/[0.08] via-white/[0.04] to-white/[0.02] border border-white/[0.12] hover:border-[#FF5A1F]/60 shadow-[0_20px_50px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.15)] hover:shadow-[0_25px_60px_-10px_rgba(255,90,31,0.25),inset_0_1px_0_rgba(255,255,255,0.25)] backdrop-blur-2xl transition-all duration-300 ease-out select-none cursor-default overflow-hidden"
        style={{
          transformStyle: "preserve-3d",
          willChange: "transform",
        }}
      >
        {/* Soft radial corner glow on hover */}
        <div className="absolute -top-20 -right-20 w-44 h-44 bg-[#FF5A1F]/15 rounded-full blur-3xl pointer-events-none group-hover:bg-[#FF5A1F]/30 group-hover:scale-125 transition-all duration-500" />
        
        {/* Subtle bottom-left ambient fill */}
        <div className="absolute -bottom-16 -left-16 w-36 h-36 bg-white/[0.03] rounded-full blur-2xl pointer-events-none" />

        <div className="relative z-10">
          {children}
        </div>
      </div>
    </Reveal>
  );
}

export const Numbers = () => (
  <section className="py-12 sm:py-18 px-5 sm:px-8 bg-[#0B0B0C] border-y border-white/[0.08] relative overflow-hidden">
    {/* Background ambient lighting */}
    <div
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[360px] bg-gradient-to-r from-[#FF5A1F]/15 via-orange-500/10 to-amber-500/10 blur-[140px] rounded-full pointer-events-none"
    />
    {/* Subtle technical dot matrix */}
    <div
      className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:24px_24px] pointer-events-none opacity-60"
    />

    <div className="max-w-6xl mx-auto relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {STATS.slice(0, 3).map((s, i) => {
          const Icon = STAT_ICONS[i] || Zap;
          const isProjects = i === 0;

          return (
            <TiltStatCard key={s.label} testId={`stat-${i}`} delay={i * 0.08}>
              <div className="flex items-center justify-between mb-7 sm:mb-8">
                <div className="w-12 h-12 sm:w-13 sm:h-13 rounded-2xl bg-gradient-to-br from-[#FF5A1F]/20 to-[#FF5A1F]/5 border border-[#FF5A1F]/30 flex items-center justify-center text-[#FF5A1F] shadow-inner group-hover:scale-110 transition-transform duration-300">
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#FF5A1F]" />
                </div>
                {isProjects && (
                  <span className="inline-flex items-center gap-1.5 text-[10.5px] font-mono font-semibold tracking-wider uppercase px-3 py-1 rounded-full bg-[#FF5A1F]/15 text-[#FF5A1F] border border-[#FF5A1F]/30 shadow-xs">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] animate-pulse" />
                    Verified
                  </span>
                )}
              </div>

              <div className="font-display text-5xl sm:text-6xl font-black text-white leading-none tracking-tight drop-shadow-md mb-3">
                <CountUp end={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
              </div>

              <div className="font-heading text-lg sm:text-xl font-semibold text-white/90 tracking-tight">
                {isProjects ? "Projects Delivered" : s.label}
              </div>
            </TiltStatCard>
          );
        })}
      </div>
    </div>
  </section>
);

/* What Do We Do — clean introduction to services with team office background */
export const WhatDoWeDo = () => (
  <section className="py-8 sm:py-14 px-5 sm:px-8 relative overflow-hidden border-t border-white/10 bg-[#0B0B0B]" id="what-we-do">
    {/* Background Image — more vivid and visible */}
    <div className="absolute inset-0 z-0">
      <img
        src="/brand/what-we-do-bg.png"
        alt="Engineering Team Workspace"
        className="w-full h-full object-cover object-center filter brightness-105"
      />
      {/* Soft directional gradient: dark on left for text legibility, clear and open on the right so office and team are prominently visible */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-transparent" />
      <div className="absolute inset-0 bg-black/15" />
    </div>

    <div className="max-w-7xl mx-auto relative z-10">
      <Reveal delay={0.1}>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.1] text-white mb-4 drop-shadow-md">
          What Do <span className="accent-underline">We Do</span>
        </h2>
      </Reveal>
      <Reveal delay={0.2}>
        <div className="space-y-3 max-w-3xl">
          <p className="text-base sm:text-lg text-white/95 leading-relaxed font-normal drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)]">
            We build AI solutions that help your business thrive and be ready for the changing times! We engineer, deploy, secure and continuously monitor your business at the backend so that you can focus on delivering and thriving at the forefront.
          </p>
          <p className="text-base sm:text-lg text-white/95 leading-relaxed font-normal drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)]">
            Whether you are looking for AI enterprise platforms, cloud infrastructure, or maintenance for your already thriving web/application, we are here to help!
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ───────────────────────────────────────────────────────────── */
/* Curved Services Carousel (FixelsMedia-style 3D circular arc)  */
/* ───────────────────────────────────────────────────────────── */
export const CurvedServicesCarousel = ({ items }) => {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const cardsRef = useRef([]);
  const isHoveredRef = useRef(false);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartPosRef = useRef(0);
  const hasMovedRef = useRef(false);
  const animFrameRef = useRef(null);
  const posRef = useRef(0);
  const lastTimeRef = useRef(null);
  const metricsRef = useRef({ singleWidth: 0, cardSpacing: 0, firstLeft: 0, cardWidth: 0 });

  // Repeat items to ensure seamless infinite loop
  const repeatCount = items.length <= 2 ? 6 : items.length <= 4 ? 4 : 3;
  const repeatedItems = useMemo(() => {
    const list = [];
    for (let i = 0; i < repeatCount; i++) {
      items.forEach((item, originalIndex) => {
        list.push({ ...item, keyId: `${item.id}-${i}-${originalIndex}` });
      });
    }
    return list;
  }, [items, repeatCount]);

  // Recalculate track geometry
  const measure = useCallback(() => {
    const container = containerRef.current;
    const cards = cardsRef.current.filter(Boolean);
    if (!container || cards.length < 2) return;

    const firstCard = cards[0];
    const secondCard = cards[1];
    const cardSpacing = secondCard.offsetLeft - firstCard.offsetLeft;
    const cardWidth = firstCard.offsetWidth;
    const singleWidth = cardSpacing * items.length;

    metricsRef.current = {
      singleWidth: singleWidth > 0 ? singleWidth : 1,
      cardSpacing,
      firstLeft: firstCard.offsetLeft,
      cardWidth,
    };
  }, [items.length]);

  useEffect(() => {
    measure();
    const t = setTimeout(measure, 120);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
    };
  }, [measure, repeatedItems]);

  // Animation Loop (60/120fps continuous left-to-right circular curved motion)
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Speed in pixels per second: moving left to right
    const speed = 50;

    const tick = (time) => {
      if (lastTimeRef.current === null) lastTimeRef.current = time;
      const dt = Math.min((time - lastTimeRef.current) / 1000, 0.1);
      lastTimeRef.current = time;

      const { singleWidth, cardSpacing, firstLeft, cardWidth } = metricsRef.current;
      const container = containerRef.current;
      const track = trackRef.current;

      if (container && track && singleWidth > 1) {
        // Increment position to move LEFT TO RIGHT (positive transform)
        if (!prefersReducedMotion && !isHoveredRef.current && !isDraggingRef.current) {
          posRef.current += speed * dt;
          if (posRef.current >= singleWidth) {
            posRef.current -= singleWidth;
          }
        }

        // Apply track translation
        const trackX = posRef.current - singleWidth;
        track.style.transform = `translate3d(${trackX}px, 0, 0)`;

        // Apply 3D circular curved projection to each card
        const containerWidth = container.clientWidth;
        const centerX = containerWidth / 2;
        const halfWidth = Math.max(centerX, 1);

        cardsRef.current.forEach((cardEl, idx) => {
          if (!cardEl) return;
          const cardCenterScreen = firstLeft + idx * cardSpacing + cardWidth / 2 + trackX;
          const normDist = Math.max(-1.4, Math.min(1.4, (cardCenterScreen - centerX) / halfWidth));

          // Vertical circular arc (curves upward towards the edges, center dips down lower)
          const yCurve = -Math.pow(normDist, 2) * 32;
          // 3D cylindrical rotation facing the viewer
          const rotateY = -(18 * normDist);
          // Tangent tilt along the circular curve
          const rotateZ = normDist * 3.5;
          // Smooth prominent scale in center
          const scale = 1 - Math.min(0.12, Math.pow(normDist, 2) * 0.08);

          cardEl.style.transform = `translateY(${yCurve}px) perspective(1000px) rotateY(${rotateY}deg) rotateZ(${rotateZ}deg) scale(${scale})`;
          cardEl.style.zIndex = Math.round(30 - Math.abs(normDist) * 10);
        });
      }

      animFrameRef.current = requestAnimationFrame(tick);
    };

    animFrameRef.current = requestAnimationFrame(tick);
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  // Pointer drag/swipe handling
  const handlePointerDown = (e) => {
    isDraggingRef.current = true;
    dragStartXRef.current = e.clientX;
    dragStartPosRef.current = posRef.current;
    hasMovedRef.current = false;
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return;
    const deltaX = e.clientX - dragStartXRef.current;
    if (Math.abs(deltaX) > 4) {
      hasMovedRef.current = true;
    }
    const { singleWidth } = metricsRef.current;
    if (singleWidth > 0) {
      let newPos = dragStartPosRef.current + deltaX;
      while (newPos < 0) newPos += singleWidth;
      while (newPos >= singleWidth) newPos -= singleWidth;
      posRef.current = newPos;
    }
  };

  const handlePointerUp = () => {
    isDraggingRef.current = false;
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden pt-12 pb-14 select-none"
      style={{
        maskImage: "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
        WebkitMaskImage: "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
        perspective: "1200px",
      }}
      onMouseEnter={() => { isHoveredRef.current = true; }}
      onMouseLeave={() => { isHoveredRef.current = false; isDraggingRef.current = false; }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div
        ref={trackRef}
        className="flex w-max items-center gap-4 sm:gap-6 will-change-transform cursor-grab active:cursor-grabbing"
      >
        {repeatedItems.map((u, i) => {
          const Icon = u.icon;
          const CardWrapper = u.isExternal ? "a" : Link;
          const wrapperProps = u.isExternal
            ? { href: u.link, target: "_blank", rel: "noopener noreferrer" }
            : { to: u.link };

          return (
            <div
              key={u.keyId}
              ref={(el) => { cardsRef.current[i] = el; }}
              className="shrink-0 w-[290px] sm:w-[330px] md:w-[360px] h-[320px] sm:h-[340px] will-change-transform"
            >
              <CardWrapper
                {...wrapperProps}
                onClick={(e) => {
                  if (hasMovedRef.current) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                className="group gborder glass-card rounded-2xl p-5 sm:p-6 h-full flex flex-col justify-between transition-colors duration-300 hover:border-[#FF5A1F]/40 block cursor-pointer select-none bg-white/90 backdrop-blur-md shadow-lg shadow-black/[0.04]"
              >
                <div>
                  {/* Top Bar: Brand Logo or Icon Box */}
                  <div className="flex items-center mb-4 min-h-[42px]">
                    {u.brandLogo ? (
                      <div className="flex items-center justify-start shrink-0">
                        <img
                          src={u.brandLogo}
                          alt={u.brandAlt || u.title}
                          className="h-8 sm:h-9 w-auto max-w-[150px] object-contain transition-transform duration-300 group-hover:scale-105 pointer-events-none"
                        />
                      </div>
                    ) : (
                      <span className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#FF5A1F]/10 border border-[#FF5A1F]/20 grid place-items-center text-[#FF5A1F] group-hover:bg-[#FF5A1F] group-hover:text-white transition-colors duration-300 shrink-0">
                        <Icon className="w-5 h-5" />
                      </span>
                    )}
                  </div>

                  {/* Card Title */}
                  <h3 className="font-display text-xl font-bold text-[#0B0B0B] group-hover:text-[#FF5A1F] transition-colors mb-2">
                    {u.title}
                  </h3>

                  {/* 1-sentence description */}
                  <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed mb-4 line-clamp-3">
                    {u.desc}
                  </p>
                </div>

                {/* Bottom Bar: Action Link */}
                <div className="pt-3 border-t border-black/5 flex items-center justify-end gap-2 mt-auto">
                  <div className="text-xs font-semibold text-[#FF5A1F] group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 shrink-0">
                    <span>{u.actionLabel || (u.isExternal ? "Visit" : "Explore")}</span>
                    {u.isExternal ? <ExternalLink className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
                  </div>
                </div>
              </CardWrapper>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ServicesShowcase = ({ isPage = false }) => {
  const [filter, setFilter] = useState("all");

  const displayedItems = ALL_OFFERINGS.filter((item) => {
    if (filter === "services") return item.type === "service";
    if (filter === "products") return item.type === "product";
    return true;
  });

  return (
    <section className={`${isPage ? "py-10 sm:py-16 pt-8 sm:pt-12" : "py-6 sm:py-10"} px-5 sm:px-8 mesh-light overflow-hidden`} id="services">
      <div className="max-w-7xl mx-auto">
        {/* Main Section Header or Page Filter Header */}
        {!isPage ? (
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-4 sm:mb-6">
            <SectionHead
              className="mb-0 max-w-3xl"
              eyebrow="WHAT SERVICES DO WE PROVIDE"
              title={<>Products & Services Curated for <span className="text-gradient">Growth</span></>}
            />

            {/* Interactive Filter Pills */}
            <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-black/5 border border-black/5 self-start md:self-end shrink-0">
              <button
                onClick={() => setFilter("all")}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  filter === "all"
                    ? "bg-[#FF5A1F] text-white shadow-md shadow-[#FF5A1F]/20"
                    : "text-neutral-600 hover:text-black hover:bg-black/5"
                }`}
              >
                All ({ALL_OFFERINGS.length})
              </button>
              <button
                onClick={() => setFilter("services")}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  filter === "services"
                    ? "bg-[#FF5A1F] text-white shadow-md shadow-[#FF5A1F]/20"
                    : "text-neutral-600 hover:text-black hover:bg-black/5"
                }`}
              >
                Services (6)
              </button>
              <button
                onClick={() => setFilter("products")}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  filter === "products"
                    ? "bg-[#FF5A1F] text-white shadow-md shadow-[#FF5A1F]/20"
                    : "text-neutral-600 hover:text-black hover:bg-black/5"
                }`}
              >
                Products (2)
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between flex-wrap gap-4 mb-10 pb-6 border-b border-black/5">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#FF5A1F] animate-pulse" />
              <p className="text-sm font-semibold text-neutral-800">
                All 8 Unified Capabilities & Products
              </p>
            </div>
            <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-black/5 border border-black/5">
              <button
                onClick={() => setFilter("all")}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  filter === "all"
                    ? "bg-[#FF5A1F] text-white shadow-md shadow-[#FF5A1F]/20"
                    : "text-neutral-600 hover:text-black hover:bg-black/5"
                }`}
              >
                All ({ALL_OFFERINGS.length})
              </button>
              <button
                onClick={() => setFilter("services")}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  filter === "services"
                    ? "bg-[#FF5A1F] text-white shadow-md shadow-[#FF5A1F]/20"
                    : "text-neutral-600 hover:text-black hover:bg-black/5"
                }`}
              >
                Services (6)
              </button>
              <button
                onClick={() => setFilter("products")}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  filter === "products"
                    ? "bg-[#FF5A1F] text-white shadow-md shadow-[#FF5A1F]/20"
                    : "text-neutral-600 hover:text-black hover:bg-black/5"
                }`}
              >
                Products (2)
              </button>
            </div>
          </div>
        )}
      </div>


        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {displayedItems.map((u, i) => {
              const Icon = u.icon;
              const CardWrapper = u.isExternal ? "a" : Link;
              const wrapperProps = u.isExternal
                ? { href: u.link, target: "_blank", rel: "noopener noreferrer" }
                : { to: u.link };

              return (
                <Reveal key={u.id} delay={(i % 3) * 0.05}>
                  <CardWrapper
                    {...wrapperProps}
                    className="group gborder glass-card rounded-2xl p-6 sm:p-7 h-full flex flex-col justify-between transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:border-[#FF5A1F]/40 block cursor-pointer"
                  >
                    <div>
                      {/* Top Bar: Brand Logo on Left (replaces mock icon) */}
                      <div className="flex items-center mb-5 min-h-[44px] sm:min-h-[48px]">
                        {u.brandLogo ? (
                          <div className="flex items-center justify-start shrink-0">
                            <img
                              src={u.brandLogo}
                              alt={u.brandAlt || u.title}
                              className="h-7 sm:h-8 md:h-9 w-auto max-w-[130px] sm:max-w-[150px] md:max-w-[170px] object-contain transition-transform duration-300 group-hover:scale-105"
                            />
                          </div>
                        ) : (
                          <span className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#FF5A1F]/10 border border-[#FF5A1F]/20 grid place-items-center text-[#FF5A1F] group-hover:bg-[#FF5A1F] group-hover:text-white transition-colors duration-300 shrink-0">
                            <Icon className="w-5 h-5" />
                          </span>
                        )}
                      </div>

                      {/* Card Title */}
                      <h3 className="font-display text-xl font-bold text-[#0B0B0B] group-hover:text-[#FF5A1F] transition-colors mb-2">
                        {u.title}
                      </h3>

                      {/* Concise 1-sentence description */}
                      <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed mb-5">
                        {u.desc}
                      </p>
                    </div>

                    {/* Bottom Bar: Action Link */}
                    <div className="pt-4 border-t border-black/5 flex items-center justify-end gap-2 mt-auto">
                      <div className="text-xs font-semibold text-[#FF5A1F] group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 shrink-0">
                        <span>{u.actionLabel || (u.isExternal ? "Visit" : "Explore")}</span>
                        {u.isExternal ? <ExternalLink className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
                      </div>
                    </div>
                  </CardWrapper>
                </Reveal>
              );
            })}
          </div>
        </div>
    </section>
  );
};

export const NorbitShowcase = () => null;


/* Services grid - unified offerings */
export const ServicesGrid = ({ heading = true }) => (
  <ServicesShowcase isPage={!heading} />
);



/* ─── Animated Industry Icons ─────────────────────────────────────────────── */
/* Each icon is a bespoke animated SVG with crisp default geometry & fluid hover micro-animations */

function IndIcon({ children, size = 36 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 52 52"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="transition-transform duration-300"
    >
      {children}
    </svg>
  );
}

/* Healthcare — cardiology ECG pulse line with live rhythm */
const IcoHealthcare = ({ h }) => (
  <IndIcon>
    <circle
      cx="26"
      cy="26"
      r="22"
      stroke={h ? "#FF5A1F" : "#334155"}
      strokeWidth="1"
      strokeDasharray="3 3"
      opacity={h ? 0.6 : 0.25}
    />
    <motion.path
      d="M6 26 L13 26 L17 14 L22 38 L26 20 L30 26 L36 26 L40 18 L44 26 L46 26"
      stroke={h ? "#FF5A1F" : "#cbd5e1"}
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      animate={{
        scale: h ? [1, 1.05, 1] : 1,
      }}
      transition={{ duration: 0.9, repeat: h ? Infinity : 0, ease: "easeInOut" }}
    />
    {h && (
      <motion.circle
        cx="22"
        cy="38"
        r="3"
        fill="#FF5A1F"
        animate={{ scale: [1, 1.8, 1], opacity: [0.9, 0.2, 0.9] }}
        transition={{ duration: 0.8, repeat: Infinity }}
      />
    )}
  </IndIcon>
);

/* Education — graduation mortarboard with swinging tassel */
const IcoEducation = ({ h }) => (
  <IndIcon>
    <motion.g
      animate={{ y: h ? -2 : 0, rotate: h ? [-2, 2, -2] : 0 }}
      transition={{ duration: 1.2, repeat: h ? Infinity : 0, ease: "easeInOut" }}
      style={{ originX: "26px", originY: "20px" }}
    >
      <polygon
        points="26,10 46,20 26,30 6,20"
        stroke={h ? "#FF5A1F" : "#cbd5e1"}
        strokeWidth="2.2"
        fill={h ? "rgba(255,90,31,0.14)" : "rgba(255,255,255,0.03)"}
        strokeLinejoin="round"
      />
      <path
        d="M17 26 L17 35 C17 38 21 41 26 41 C31 41 35 38 35 35 L35 26"
        stroke={h ? "#FF5A1F" : "#94a3b8"}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
    </motion.g>
    <motion.g
      animate={{ rotate: h ? [-16, 16, -16] : 0 }}
      transition={{ duration: 0.8, repeat: h ? Infinity : 0, ease: "easeInOut" }}
      style={{ originX: "44px", originY: "20px" }}
    >
      <line x1="44" y1="20" x2="44" y2="34" stroke={h ? "#FF5A1F" : "#cbd5e1"} strokeWidth="2" strokeLinecap="round" />
      <circle cx="44" cy="36" r="3" fill={h ? "#FF5A1F" : "#cbd5e1"} />
    </motion.g>
  </IndIcon>
);

/* B2B Industry — modern corporate skyline & enterprise infrastructure */
const IcoB2B = ({ h }) => (
  <IndIcon>
    {/* Ground baseline */}
    <line
      x1="5"
      y1="43.5"
      x2="47"
      y2="43.5"
      stroke={h ? "#FF5A1F" : "#64748b"}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <motion.g
      animate={{ y: h ? -2 : 0 }}
      transition={{ duration: 0.6, repeat: h ? Infinity : 0, repeatType: "reverse", ease: "easeInOut" }}
    >
      {/* Left low-rise enterprise building */}
      <path
        d="M8.5 43.5 V24.5 H18.5 V43.5"
        stroke={h ? "#FF5A1F" : "#cbd5e1"}
        strokeWidth="2.2"
        strokeLinejoin="round"
        fill={h ? "rgba(255,90,31,0.14)" : "none"}
      />
      {/* Left building horizontal window slits */}
      <line x1="11.5" y1="29.5" x2="15.5" y2="29.5" stroke={h ? "#FF5A1F" : "#94a3b8"} strokeWidth="1.8" strokeLinecap="round" />
      <line x1="11.5" y1="34" x2="15.5" y2="34" stroke={h ? "#FF5A1F" : "#94a3b8"} strokeWidth="1.8" strokeLinecap="round" />
      <line x1="11.5" y1="38.5" x2="15.5" y2="38.5" stroke={h ? "#FF5A1F" : "#94a3b8"} strokeWidth="1.8" strokeLinecap="round" />

      {/* Center commercial skyscraper with angled crown */}
      <path
        d="M18.5 24.5 V19 L26.5 9 L32.5 13 V27"
        stroke={h ? "#FF5A1F" : "#cbd5e1"}
        strokeWidth="2.2"
        strokeLinejoin="round"
        strokeLinecap="round"
        fill={h ? "rgba(255,90,31,0.18)" : "none"}
      />
      {/* Center skyscraper vertical window lines */}
      <line x1="22" y1="21" x2="22" y2="40" stroke={h ? "#FF5A1F" : "#94a3b8"} strokeWidth="1.9" strokeLinecap="round" />
      <line x1="25.5" y1="21" x2="25.5" y2="40" stroke={h ? "#FF5A1F" : "#94a3b8"} strokeWidth="1.9" strokeLinecap="round" />
      <line x1="29" y1="16" x2="29" y2="23" stroke={h ? "#FF5A1F" : "#94a3b8"} strokeWidth="1.7" strokeLinecap="round" />
      <line x1="31" y1="18" x2="31" y2="23" stroke={h ? "#FF5A1F" : "#94a3b8"} strokeWidth="1.7" strokeLinecap="round" />

      {/* Right facility with pitched roof */}
      <path
        d="M32.5 27 L43.5 31 V43.5"
        stroke={h ? "#FF5A1F" : "#cbd5e1"}
        strokeWidth="2.2"
        strokeLinejoin="round"
        strokeLinecap="round"
        fill={h ? "rgba(255,90,31,0.14)" : "none"}
      />
      {/* Right facility doorway */}
      <path
        d="M37.5 43.5 V35.5 H41 V43.5"
        stroke={h ? "#FF5A1F" : "#94a3b8"}
        strokeWidth="1.8"
        strokeLinejoin="round"
        fill={h ? "rgba(255,90,31,0.35)" : "none"}
      />
    </motion.g>
  </IndIcon>
);

/* Finance / Alternate */
const IcoFinance = ({ h }) => (
  <IndIcon>
    <motion.g
      animate={{ rotateY: h ? 360 : 0 }}
      transition={{ duration: 1.2, repeat: h ? Infinity : 0, repeatDelay: 0.5, ease: "easeInOut" }}
      style={{ originX: "26px", originY: "26px" }}
    >
      <circle cx="26" cy="26" r="18" stroke={h ? "#FF5A1F" : "#cbd5e1"} strokeWidth="2.2" fill={h ? "rgba(255,90,31,0.12)" : "none"} />
      <circle cx="26" cy="26" r="14" stroke={h ? "#FF5A1F" : "#64748b"} strokeWidth="1.2" strokeDasharray="3 2" fill="none" />
      <text
        x="26"
        y="32"
        textAnchor="middle"
        fontSize="16"
        fontWeight="800"
        fontFamily="system-ui, sans-serif"
        fill={h ? "#FF5A1F" : "#cbd5e1"}
        style={{ userSelect: "none" }}
      >
        $
      </text>
    </motion.g>
    <path d="M10 44 Q26 48 42 44" stroke={h ? "#FF5A1F" : "#475569"} strokeWidth="1.8" strokeLinecap="round" fill="none" />
  </IndIcon>
);

/* Real Estate — clean architecture silhouette with illuminated windows */
const IcoRealEstate = ({ h }) => (
  <IndIcon>
    <motion.path
      d="M8 24 L26 8 L44 24 L44 44 L8 44 Z"
      stroke={h ? "#FF5A1F" : "#cbd5e1"}
      strokeWidth="2.2"
      strokeLinejoin="round"
      fill={h ? "rgba(255,90,31,0.08)" : "none"}
      animate={{ scale: h ? [1, 1.02, 1] : 1 }}
      transition={{ duration: 0.8, repeat: h ? Infinity : 0 }}
      style={{ originX: "26px", originY: "26px" }}
    />
    <rect x="32" y="11" width="5" height="7" rx="1" stroke={h ? "#FF5A1F" : "#94a3b8"} strokeWidth="1.8" fill="none" />
    <motion.rect
      x="13"
      y="26"
      width="8"
      height="7"
      rx="1.5"
      stroke={h ? "#FF5A1F" : "#94a3b8"}
      strokeWidth="1.6"
      fill={h ? "rgba(255,90,31,0.45)" : "rgba(255,255,255,0.06)"}
      animate={{ opacity: h ? [0.6, 1, 0.6] : 1 }}
      transition={{ duration: 1, repeat: h ? Infinity : 0 }}
    />
    <motion.rect
      x="31"
      y="26"
      width="8"
      height="7"
      rx="1.5"
      stroke={h ? "#FF5A1F" : "#94a3b8"}
      strokeWidth="1.6"
      fill={h ? "rgba(255,90,31,0.45)" : "rgba(255,255,255,0.06)"}
      animate={{ opacity: h ? [0.6, 1, 0.6] : 1 }}
      transition={{ duration: 1, delay: 0.2, repeat: h ? Infinity : 0 }}
    />
    <path d="M22 44 L22 34 Q26 31 30 34 L30 44" stroke={h ? "#FF5A1F" : "#cbd5e1"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <circle cx="28.5" cy="38" r="1.2" fill={h ? "#FF5A1F" : "#cbd5e1"} />
  </IndIcon>
);

/* Retail — swinging shopping bag */
const IcoRetail = ({ h }) => (
  <IndIcon>
    <motion.g
      animate={{ rotate: h ? [-6, 6, -6] : 0 }}
      transition={{ duration: 0.9, repeat: h ? Infinity : 0, ease: "easeInOut" }}
      style={{ originX: "26px", originY: "12px" }}
    >
      <path
        d="M14 18 L10 44 L42 44 L38 18 Z"
        stroke={h ? "#FF5A1F" : "#cbd5e1"}
        strokeWidth="2.2"
        strokeLinejoin="round"
        fill={h ? "rgba(255,90,31,0.1)" : "none"}
      />
      <path
        d="M20 18 C20 10 32 10 32 18"
        stroke={h ? "#FF5A1F" : "#cbd5e1"}
        strokeWidth="2.2"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="26" cy="28" r="3" fill={h ? "#FF5A1F" : "#64748b"} />
      <line x1="26" y1="31" x2="26" y2="37" stroke={h ? "#FF5A1F" : "#64748b"} strokeWidth="1.8" strokeLinecap="round" />
    </motion.g>
  </IndIcon>
);

/* Manufacturing — precision industrial gear with continuous rotation */
const IcoManufacturing = ({ h }) => (
  <IndIcon>
    <motion.g
      animate={{ rotate: h ? 360 : 0 }}
      transition={{ duration: 2.8, repeat: h ? Infinity : 0, ease: "linear" }}
      style={{ originX: "26px", originY: "26px" }}
    >
      <circle cx="26" cy="26" r="9" stroke={h ? "#FF5A1F" : "#cbd5e1"} strokeWidth="2.2" fill="none" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, idx) => {
        const rad = (angle * Math.PI) / 180;
        const x1 = 26 + 10 * Math.cos(rad);
        const y1 = 26 + 10 * Math.sin(rad);
        const x2 = 26 + 16 * Math.cos(rad);
        const y2 = 26 + 16 * Math.sin(rad);
        return (
          <line
            key={idx}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={h ? "#FF5A1F" : "#cbd5e1"}
            strokeWidth="3.2"
            strokeLinecap="round"
          />
        );
      })}
    </motion.g>
    <circle cx="26" cy="26" r="4.5" fill={h ? "#FF5A1F" : "#64748b"} />
  </IndIcon>
);

/* Logistics — delivery cargo truck with speed lines */
const IcoLogistics = ({ h }) => (
  <IndIcon>
    <motion.g
      animate={{ x: h ? [0, 2.5, 0] : 0, y: h ? [0, -1, 0] : 0 }}
      transition={{ duration: 0.6, repeat: h ? Infinity : 0, ease: "easeInOut" }}
    >
      <rect x="6" y="18" width="24" height="18" rx="2.5" stroke={h ? "#FF5A1F" : "#cbd5e1"} strokeWidth="2.2" fill="none" />
      <path d="M30 22 L40 22 L46 30 L46 36 L30 36 Z" stroke={h ? "#FF5A1F" : "#cbd5e1"} strokeWidth="2.2" fill="none" strokeLinejoin="round" />
      <line x1="33" y1="28" x2="39" y2="28" stroke={h ? "#FF5A1F" : "#94a3b8"} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="14" cy="38" r="4.5" stroke={h ? "#FF5A1F" : "#cbd5e1"} strokeWidth="2" fill="#0d0d0f" />
      <circle cx="14" cy="38" r="2" fill={h ? "#FF5A1F" : "#64748b"} />
      <circle cx="38" cy="38" r="4.5" stroke={h ? "#FF5A1F" : "#cbd5e1"} strokeWidth="2" fill="#0d0d0f" />
      <circle cx="38" cy="38" r="2" fill={h ? "#FF5A1F" : "#64748b"} />
    </motion.g>
    <motion.line
      x1="2"
      y1="23"
      x2="4"
      y2="23"
      stroke={h ? "#FF5A1F" : "#475569"}
      strokeWidth="2"
      strokeLinecap="round"
      animate={{ x: h ? [-4, 0] : 0, opacity: h ? [1, 0] : 0.4 }}
      transition={{ duration: 0.4, repeat: h ? Infinity : 0 }}
    />
    <motion.line
      x1="1"
      y1="31"
      x2="4"
      y2="31"
      stroke={h ? "#FF5A1F" : "#475569"}
      strokeWidth="2"
      strokeLinecap="round"
      animate={{ x: h ? [-5, 0] : 0, opacity: h ? [1, 0] : 0.4 }}
      transition={{ duration: 0.4, delay: 0.1, repeat: h ? Infinity : 0 }}
    />
  </IndIcon>
);

/* Government — security shield with glowing verified checkmark */
const IcoGovernment = ({ h }) => (
  <IndIcon>
    <motion.path
      d="M26 8 L42 14 L42 27 C42 37 26 45 26 45 C26 45 10 37 10 27 L10 14 Z"
      stroke={h ? "#FF5A1F" : "#cbd5e1"}
      strokeWidth="2.2"
      fill={h ? "rgba(255,90,31,0.12)" : "rgba(255,255,255,0.03)"}
      strokeLinejoin="round"
      animate={{ scale: h ? [1, 1.05, 1] : 1 }}
      transition={{ duration: 1.1, repeat: h ? Infinity : 0 }}
      style={{ originX: "26px", originY: "26px" }}
    />
    <motion.path
      d="M19 25 L24 30 L33 20"
      stroke={h ? "#FF5A1F" : "#94a3b8"}
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      animate={{
        scale: h ? [1, 1.12, 1] : 1,
      }}
      transition={{ duration: 0.6 }}
    />
  </IndIcon>
);

/* Hospitality — hotel concierge bellhop with luggage & animated serving cloche */
const IcoHospitality = ({ h }) => (
  <IndIcon>
    {/* Ground baseline */}
    <line
      x1="5"
      y1="43.5"
      x2="47"
      y2="43.5"
      stroke={h ? "#FF5A1F" : "#64748b"}
      strokeWidth="2"
      strokeLinecap="round"
    />

    {/* Bellhop Concierge */}
    <motion.g
      animate={{ y: h ? -1.5 : 0 }}
      transition={{ duration: 0.6, repeat: h ? Infinity : 0, repeatType: "reverse", ease: "easeInOut" }}
    >
      {/* Pillbox hat */}
      <path
        d="M16 13.5 C16 9.8 24.5 9.8 24.5 13.5 Z"
        stroke={h ? "#FF5A1F" : "#cbd5e1"}
        strokeWidth="2"
        strokeLinejoin="round"
        fill={h ? "rgba(255,90,31,0.22)" : "none"}
      />
      <line x1="15" y1="14" x2="25.5" y2="14" stroke={h ? "#FF5A1F" : "#cbd5e1"} strokeWidth="2.2" strokeLinecap="round" />

      {/* Face & jawline */}
      <path
        d="M16.5 16 C16 23.5 24.5 23.5 24 16"
        stroke={h ? "#FF5A1F" : "#cbd5e1"}
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />

      {/* Uniform shoulders & chest */}
      <path
        d="M10.5 43.5 V38.5 C10.5 31 15.5 27.5 18.5 27"
        stroke={h ? "#FF5A1F" : "#cbd5e1"}
        strokeWidth="2.2"
        strokeLinecap="round"
        fill={h ? "rgba(255,90,31,0.12)" : "none"}
      />
      <path
        d="M22 27 C24.5 27.5 28.5 29.5 28.5 34"
        stroke={h ? "#FF5A1F" : "#cbd5e1"}
        strokeWidth="2.2"
        strokeLinecap="round"
        fill="none"
      />
      <line x1="20.5" y1="27" x2="20.5" y2="38" stroke={h ? "#FF5A1F" : "#94a3b8"} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="22.5" cy="30.5" r="0.9" fill={h ? "#FF5A1F" : "#94a3b8"} />
      <circle cx="22.5" cy="34.5" r="0.9" fill={h ? "#FF5A1F" : "#94a3b8"} />
    </motion.g>

    {/* Suitcase / Luggage on the right */}
    <motion.g
      animate={{ y: h ? -1 : 0 }}
      transition={{ duration: 0.6, repeat: h ? Infinity : 0, repeatType: "reverse", ease: "easeInOut", delay: 0.1 }}
    >
      <path
        d="M34.5 26.5 V23.5 H38.5 V26.5"
        stroke={h ? "#FF5A1F" : "#94a3b8"}
        strokeWidth="1.8"
        strokeLinejoin="round"
        fill="none"
      />
      <rect
        x="31"
        y="26.5"
        width="11"
        height="17"
        rx="2.5"
        stroke={h ? "#FF5A1F" : "#cbd5e1"}
        strokeWidth="2.2"
        fill={h ? "rgba(255,90,31,0.14)" : "none"}
      />
      <line x1="34.5" y1="30" x2="34.5" y2="40.5" stroke={h ? "#FF5A1F" : "#94a3b8"} strokeWidth="1.8" strokeLinecap="round" />
      <line x1="38.5" y1="30" x2="38.5" y2="40.5" stroke={h ? "#FF5A1F" : "#94a3b8"} strokeWidth="1.8" strokeLinecap="round" />
    </motion.g>

    {/* Cloche serving tray & animated dome in foreground */}
    <line x1="22" y1="41.5" x2="32" y2="41.5" stroke={h ? "#FF5A1F" : "#cbd5e1"} strokeWidth="2" strokeLinecap="round" />
    <motion.g
      animate={{ y: h ? [-3, 0] : 0 }}
      transition={{ duration: 0.8, repeat: h ? Infinity : 0, repeatType: "reverse", ease: "easeInOut" }}
    >
      <path
        d="M23.5 41.5 C23.5 36.5 30.5 36.5 30.5 41.5"
        stroke={h ? "#FF5A1F" : "#cbd5e1"}
        strokeWidth="2"
        strokeLinecap="round"
        fill={h ? "rgba(255,90,31,0.25)" : "none"}
      />
      <circle cx="27" cy="35.5" r="1.1" fill={h ? "#FF5A1F" : "#cbd5e1"} />
    </motion.g>
  </IndIcon>
);

/* Agriculture — sprouting seedling with graceful swaying leaves */
const IcoAgriculture = ({ h }) => (
  <IndIcon>
    <motion.g
      animate={{ rotate: h ? [-3.5, 3.5, -3.5] : 0 }}
      transition={{ duration: 1.2, repeat: h ? Infinity : 0, ease: "easeInOut" }}
      style={{ originX: "26px", originY: "43px" }}
    >
      <path d="M26 43 L26 21" stroke={h ? "#FF5A1F" : "#cbd5e1"} strokeWidth="2.4" strokeLinecap="round" fill="none" />
      <path
        d="M26 21 C26 21 39 19 41 11 C41 11 29 9 26 21 Z"
        stroke={h ? "#FF5A1F" : "#cbd5e1"}
        strokeWidth="2"
        fill={h ? "rgba(255,90,31,0.2)" : "rgba(255,255,255,0.04)"}
        strokeLinejoin="round"
      />
      <path
        d="M26 27 C26 27 14 23 12 13 C12 13 24 13 26 27 Z"
        stroke={h ? "#FF5A1F" : "#cbd5e1"}
        strokeWidth="2"
        fill={h ? "rgba(255,90,31,0.2)" : "rgba(255,255,255,0.04)"}
        strokeLinejoin="round"
      />
    </motion.g>
    <path d="M16 44 Q26 46 36 44" stroke={h ? "#FF5A1F" : "#64748b"} strokeWidth="2" strokeLinecap="round" fill="none" />
  </IndIcon>
);

/* Arts & Culture — artist palette with gleaming color wells */
const IcoArtsCulture = ({ h }) => (
  <IndIcon>
    <motion.g
      animate={{ rotate: h ? [0, 6, -3, 0] : 0 }}
      transition={{ duration: 1.1, repeat: h ? Infinity : 0, ease: "easeInOut" }}
      style={{ originX: "26px", originY: "26px" }}
    >
      <path
        d="M26 10 C16 10 9 17 9 27 C9 37 17 42 23 42 C25 42 27 40 27 38 C27 36 25 34 25 32 C25 29 27 27 30 27 L35 27 C41 27 44 24 44 18 C44 12 36 10 26 10 Z"
        stroke={h ? "#FF5A1F" : "#cbd5e1"}
        strokeWidth="2.2"
        fill={h ? "rgba(255,90,31,0.12)" : "none"}
        strokeLinejoin="round"
      />
      <circle cx="16" cy="21" r="2.4" fill={h ? "#FF5A1F" : "#94a3b8"} />
      <circle cx="23" cy="16" r="2.4" fill={h ? "#FF5A1F" : "#94a3b8"} />
      <circle cx="31" cy="17" r="2.4" fill={h ? "#FF5A1F" : "#94a3b8"} />
      <circle cx="37" cy="22" r="2.4" fill={h ? "#FF5A1F" : "#94a3b8"} />
      <circle cx="19" cy="33" r="3" stroke={h ? "#FF5A1F" : "#64748b"} strokeWidth="1.6" fill="none" />
    </motion.g>
  </IndIcon>
);

const IND_LIST = [
  { name: "Healthcare",    slug: "healthcare",    Ico: IcoHealthcare,    sectorKey: "Healthcare" },
  { name: "Education",     slug: "education",     Ico: IcoEducation,     sectorKey: "Education" },
  { name: "B2B Industry",  slug: "b2b-industry",  Ico: IcoB2B,           sectorKey: "B2B Industry", label: "B2B Industry" },
  { name: "Real Estate",   slug: "real-estate",   Ico: IcoRealEstate,    sectorKey: "Real Estate" },
  { name: "Retail",        slug: "retail",        Ico: IcoRetail,        sectorKey: "Retail" },
  { name: "Manufacturing", slug: "manufacturing", Ico: IcoManufacturing, sectorKey: "Manufacturing" },
  { name: "Logistics",     slug: "logistics",     Ico: IcoLogistics,     sectorKey: "Logistics" },
  { name: "Government",    slug: "government",    Ico: IcoGovernment,    sectorKey: "Government" },
  { name: "Hospitality",   slug: "hospitality",   Ico: IcoHospitality,   sectorKey: "Hospitality" },
  { name: "Agriculture",   slug: "agriculture",   Ico: IcoAgriculture,   sectorKey: "Agriculture" },
  { name: "Arts & Culture",slug: "arts-culture",  Ico: IcoArtsCulture,   sectorKey: "Arts & Culture", label: "Arts & Culture" },
];

function IndustryCard({ ind, i, isActive, onMouseEnter, onMouseLeave, isIndustriesPage = false }) {
  const { Ico } = ind;

  return (
    <motion.div
      data-testid={`industry-${i}`}
      className={`group relative rounded-2xl p-4 sm:p-5 flex flex-col items-center justify-center gap-3 cursor-pointer h-full min-h-[135px] sm:min-h-[150px] select-none transition-all duration-300 overflow-hidden ${
        isActive
          ? isIndustriesPage
            ? "bg-[#0B0B0B] border-2 border-[#FF5A1F] shadow-[0_0_30px_rgba(255,90,31,0.38)] -translate-y-2 scale-[1.02]"
            : "bg-[#181413]/95 border-2 border-[#FF5A1F] shadow-[0_0_28px_rgba(255,90,31,0.32)] -translate-y-2 scale-[1.02]"
          : isIndustriesPage
            ? "bg-[#0B0B0B] hover:bg-[#151518] border border-black/15 shadow-[0_8px_24px_rgba(0,0,0,0.18)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.26)]"
            : "bg-[#0d0d12]/75 hover:bg-[#121218]/90 border border-white/[0.08] hover:border-white/[0.18]"
      }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 380, damping: 22 }}
    >
      {/* Subtle Top Ambient Accent Beam */}
      <div
        className={`absolute inset-x-0 top-0 h-[2px] transition-all duration-300 ${
          isActive
            ? "bg-gradient-to-r from-transparent via-[#FF5A1F] to-transparent opacity-100"
            : "bg-gradient-to-r from-transparent via-white/[0.06] to-transparent opacity-40"
        }`}
      />

      {/* Ambient Radial Hover Light Spill */}
      <div
        className={`absolute inset-0 pointer-events-none transition-opacity duration-300 ${
          isActive ? "opacity-100" : "opacity-0"
        }`}
        style={{
          background: "radial-gradient(circle at 50% 30%, rgba(255,90,31,0.22) 0%, transparent 70%)",
        }}
      />

      {/* Icon Cradle Badge */}
      <div
        className={`relative z-10 w-13 h-13 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${
          isActive
            ? "bg-[#FF5A1F]/15 border border-[#FF5A1F]/60 shadow-[0_0_18px_rgba(255,90,31,0.35)] scale-105"
            : isIndustriesPage
              ? "bg-white/[0.06] border border-white/[0.12]"
              : "bg-white/[0.04] border border-white/[0.08]"
        }`}
      >
        <Ico h={isActive} />
      </div>

      {/* Sector Label */}
      <div className="relative z-10 flex flex-col items-center">
        <span
          className={`text-xs sm:text-[14px] font-semibold text-center leading-snug transition-colors duration-200 ${
            isActive ? "text-white" : "text-neutral-200"
          }`}
        >
          {ind.label || ind.name}
        </span>
      </div>
    </motion.div>
  );
}

const SHUFFLE_SECTORS = [
  "Sectors",
  "Healthcare",
  "Education",
  "B2B Industry",
  "Real Estate",
  "Retail",
  "Manufacturing",
  "Logistics",
  "Government",
  "Hospitality",
  "Agriculture",
  "Arts & Culture",
];

export const IndustriesGrid = ({ isIndustriesPage = false, hideEyebrow = false }) => {
  const [shuffleIndex, setShuffleIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Auto shuffle scrolling cycle every 2 seconds (2000ms)
  useEffect(() => {
    if (isHovered) return;
    const timer = setInterval(() => {
      setShuffleIndex((prev) => (prev + 1) % SHUFFLE_SECTORS.length);
    }, 2000);
    return () => clearInterval(timer);
  }, [isHovered]);

  const activeIndex = shuffleIndex > 0 ? shuffleIndex - 1 : -1;
  const row1 = IND_LIST.slice(0, 6);
  const row2 = IND_LIST.slice(6, 11);

  return (
    <section
      className={`py-6 sm:py-10 px-5 sm:px-8 relative overflow-hidden ${
        isIndustriesPage ? "mesh-light" : "bg-[#0B0B0B]"
      }`}
      id="industries"
    >
      {/* Background Image on Home page only */}
      {!isIndustriesPage && (
        <div className="absolute inset-0 z-0">
          <img
            src="/brand/sectors-bg.png"
            alt="Global Collaboration Across Sectors"
            className="w-full h-full object-cover object-center filter brightness-100"
          />
          {/* Directional gradient overlay: keeps the world map, lighting and team visible while ensuring cards & text pop */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/60 to-black/85" />
          <div className="absolute inset-0 bg-black/20" />
        </div>
      )}

      <div className="max-w-7xl mx-auto relative z-10">
        <SectionHead
          center={true}
          className="max-w-4xl mb-8 sm:mb-10"
          dark={!isIndustriesPage}
          eyebrow={(hideEyebrow || !isIndustriesPage) ? null : "WHO WE'VE WORKED FOR"}
          title={
            <>
              <span className="block">We Take Pride in Being</span>
              <motion.span
                layout
                className="inline-flex items-baseline justify-center whitespace-nowrap"
                transition={{ layout: { duration: 0.25, ease: "easeOut" } }}
              >
                <span>Trusted Across&nbsp;</span>
                <span className="relative inline-flex items-baseline overflow-hidden pb-1.5 pt-0.5">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={SHUFFLE_SECTORS[shuffleIndex]}
                      initial={{ y: "110%", opacity: 0 }}
                      animate={{ y: "0%", opacity: 1 }}
                      exit={{ y: "-110%", opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className="inline-block text-gradient whitespace-nowrap"
                    >
                      {SHUFFLE_SECTORS[shuffleIndex]}
                    </motion.span>
                  </AnimatePresence>
                </span>
              </motion.span>
            </>
          }
        />

        {/* Desktop Layout: 6 cards in Row 1, 5 cards horizontally centered in Row 2 */}
        <div className="hidden lg:flex lg:flex-col lg:gap-2.5 max-w-6xl mx-auto">
          {/* Row 1 (6 cards) */}
          <div className="grid grid-cols-6 gap-4">
            {row1.map((ind, i) => (
              <Link
                key={ind.name}
                to={`/industries/${ind.slug}`}
                className="block h-full outline-none focus:ring-2 focus:ring-[#FF5A1F]/50 rounded-2xl group transition-transform duration-200"
                title={`Inspect ${ind.label || ind.name} Deployments`}
              >
                <IndustryCard
                  ind={ind}
                  i={i}
                  isActive={activeIndex === i}
                  isIndustriesPage={isIndustriesPage}
                  onMouseEnter={() => {
                    setIsHovered(true);
                    setShuffleIndex(i + 1);
                  }}
                  onMouseLeave={() => {
                    setIsHovered(false);
                  }}
                />
              </Link>
            ))}
          </div>

          {/* Row 2 (5 cards centered) */}
          <div className="flex justify-center gap-4">
            {row2.map((ind, idx) => {
              const i = idx + 6;
              return (
                <div key={ind.name} className="w-[calc((100%-5*1rem)/6)]">
                  <Link
                    to={`/industries/${ind.slug}`}
                    className="block h-full outline-none focus:ring-2 focus:ring-[#FF5A1F]/50 rounded-2xl group transition-transform duration-200"
                    title={`Inspect ${ind.label || ind.name} Deployments`}
                  >
                    <IndustryCard
                      ind={ind}
                      i={i}
                      isActive={activeIndex === i}
                      isIndustriesPage={isIndustriesPage}
                      onMouseEnter={() => {
                        setIsHovered(true);
                        setShuffleIndex(i + 1);
                      }}
                      onMouseLeave={() => {
                        setIsHovered(false);
                      }}
                    />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile & Tablet Grid (Responsive 2 to 3 columns) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-2 lg:hidden max-w-6xl mx-auto">
          {IND_LIST.map((ind, i) => (
            <Link
              key={ind.name}
              to={`/industries/${ind.slug}`}
              className="block h-full outline-none focus:ring-2 focus:ring-[#FF5A1F]/50 rounded-2xl group transition-transform duration-200"
              title={`Inspect ${ind.label || ind.name} Deployments`}
            >
              <IndustryCard
                ind={ind}
                i={i}
                isActive={activeIndex === i}
                isIndustriesPage={isIndustriesPage}
                onMouseEnter={() => {
                  setIsHovered(true);
                  setShuffleIndex(i + 1);
                }}
                onMouseLeave={() => {
                  setIsHovered(false);
                }}
              />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/* Solutions — premium glass */
export const SolutionsGrid = () => (
  <section className="py-14 sm:py-28 px-5 sm:px-8 mesh-light" id="solutions">
    <div className="max-w-7xl mx-auto">
      <SectionHead
        eyebrow="What we build"
        title="Services that move the needle"
        sub="From idea to production, the most in-demand digital capabilities delivered end-to-end."
      />


      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {SOLUTIONS.map((s, i) => {
          const Icon = s.icon;
          const isHot = s.tag === "Trending" || s.tag === "Most Popular" || s.tag === "Top Demand";
          return (
            <Reveal key={s.title} delay={(i % 3) * 0.07}>
              <Spotlight className="group relative gborder glass-card rounded-3xl p-8 h-full flex flex-col gap-4 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_24px_60px_-20px_rgba(255,90,31,0.22)]">

                {/* row: number + tag */}
                <div className="flex items-center justify-between">
                  <span className="font-display text-4xl font-black text-[#FF5A1F]/20 leading-none select-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${
                    isHot
                      ? "bg-[#FF5A1F]/12 text-[#FF5A1F]"
                      : "bg-black/5 text-neutral-400"
                  }`}>
                    {s.tag}
                  </span>
                </div>

                {/* title */}
                <div>
                  <h3 className="font-heading text-xl font-bold text-[#0B0B0B] leading-tight mb-2">{s.title}</h3>
                  <p className="text-neutral-500 text-sm leading-relaxed flex-1">{s.desc}</p>
                </div>


                {/* bottom cta hint */}
                <div className="flex items-center gap-1.5 text-[#FF5A1F] text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 -mb-1">
                  <span>Learn more</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              </Spotlight>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);


/* Featured project — big device mockups + floating stats (Home) */
export const FeaturedProject = () => {
  const p = PROJECTS.find((x) => x.slug === "buildhealer") || PROJECTS[0];

  return (
    <section className="py-14 sm:py-28 px-5 sm:px-8 grad-warm relative overflow-hidden">
      <div className="blob w-[520px] h-[520px] bg-[#FF5A1F] top-10 -left-20 animate-aurora" style={{ opacity: 0.18 }} />
      <div className="max-w-7xl mx-auto relative z-10 grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <span className="text-[#FF5A1F] font-heading text-sm font-semibold uppercase tracking-[0.22em]">Featured product</span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-white mt-4 leading-tight">{p.title}</h2>
          <p className="mt-5 text-lg text-neutral-400 leading-relaxed">{p.overview}</p>
          <div className="flex flex-wrap gap-2 mt-6">{p.tech.map((t) => <span key={t} className="text-sm px-3.5 py-1.5 rounded-full glass-card-dark text-neutral-200">{t}</span>)}</div>
          <Link to={`/portfolio/${p.slug}`} className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#FF5A1F] text-white font-semibold hover:bg-[#ff6d38] transition-colors group">View case study <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></Link>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative">
            <BrowserMock src={p.img} alt={p.title} className="aspect-[16/10]" />
            <PhoneMock src="https://images.unsplash.com/photo-1526498460520-4c246339dccb?w=400&q=80" alt="mobile" className="absolute -bottom-8 -right-4 w-28 sm:w-36 aspect-[9/19]" />
            <FloatingStat value="+38%" label="Delivery velocity" className="absolute -top-6 -left-4" delay={0.2} />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const EXCLUDED_PROJECT_SLUGS = ["woodwelt", "mycitydekho", "digital-india-rural-iot"];

/* Portfolio grid — larger cards, browser-framed */
export const PortfolioGrid = ({ items, heading = true, isHome = false }) => {
  const [projectList, setProjectList] = useState(() =>
    (items || PROJECTS).filter((p) => !EXCLUDED_PROJECT_SLUGS.includes(p.slug))
  );

  useEffect(() => {
    if (!items) {
      api.get("/projects")
        .then((res) => {
          if (Array.isArray(res.data) && res.data.length > 0) {
            setProjectList(() => {
              const merged = PROJECTS.filter((p) => !EXCLUDED_PROJECT_SLUGS.includes(p.slug));
              res.data.forEach((p) => {
                if (EXCLUDED_PROJECT_SLUGS.includes(p.slug)) return;
                if (p.slug === "meadylife" || p.slug === "medylife") {
                  p.title = "MedyLife";
                  p.logo = p.logo || "/brand/medylife-logo.png";
                } else if (p.slug === "paineasy") {
                  p.title = "PainEasy";
                  p.logo = p.logo || "/brand/paineasy-logo.svg";
                } else if (p.slug === "launchpad") {
                  p.img = "/brand/launchpad-preview.png";
                  p.logo = p.logo || "/brand/launchpad-clean.png";
                } else if (p.slug === "php2shell") {
                  p.img = "/brand/php2shell-preview.png";
                  p.logo = p.logo || "/brand/php2shell-logo.png";
                } else if (p.slug === "latajcollection") {
                  p.title = "latajcollection";
                  p.logo = "/brand/latajcollection-logo.png";
                } else if (p.slug === "wordpress-security-hardening") {
                  p.img = "/brand/wordpress-security-preview.png";
                  p.logo = p.logo || "/brand/wordpress-security-logo.png";
                }
                const idx = merged.findIndex((m) => m.slug === p.slug);
                if (idx >= 0) merged[idx] = { ...merged[idx], ...p };
                else merged.push(p);
              });
              return merged.filter((p) => !EXCLUDED_PROJECT_SLUGS.includes(p.slug));
            });
          }
        })
        .catch(() => {});
    } else {
      setProjectList(items.filter((p) => !EXCLUDED_PROJECT_SLUGS.includes(p.slug)));
    }
  }, [items]);

  const HOME_EXCLUDED_SLUGS = ["vinnskolam", "latajcollection", "dewstream"];
  const displayItems = isHome
    ? projectList.filter((p) => !HOME_EXCLUDED_SLUGS.includes(p.slug)).slice(0, 6)
    : projectList;

  return (
    <section
      className={`${
        isHome
          ? "py-6 sm:py-9 px-4 sm:px-8 bg-white border-y border-black/[0.06] relative overflow-hidden"
          : "py-10 sm:py-16 px-5 sm:px-8 mesh-light"
      }`}
      id="portfolio"
    >
      <div className="max-w-7xl mx-auto">
        {heading && (
          <SectionHead
            eyebrow={isHome ? undefined : "CLIENT IMPACT & TRANSFORMATION"}
            title={isHome ? <>What We’ve <span className="text-gradient">Built</span></> : "Enterprise Client Impact Stories"}
            sub={isHome ? "Check out our successful projects. Explore products, mobile applications, and enterprise platforms, engineered, deployed and managed by NeuraVolt." : "How we partner with industry leaders to engineer, deploy, and manage mission-critical digital systems."}
            center={isHome}
            className={isHome ? "mb-3 sm:mb-5" : "mb-6 sm:mb-8"}
          />
        )}

        {isHome ? (
          /* Draggable 3D Photo Ring (Home page only) */
          <div>
            <Draggable3DPhotoRing items={displayItems} />
            <div className="mt-4 sm:mt-6 text-center">
              <Link
                to="/portfolio"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#0B0B0B] text-white text-xs sm:text-sm font-semibold hover:bg-[#FF5A1F] transition-colors shadow-md hover:shadow-[#FF5A1F]/20 group cursor-pointer"
              >
                <span>Explore All Projects & Work</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        ) : (
          /* Standard Portfolio Grid (for /portfolio and other pages) */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayItems.map((p, i) => {
              let logoUrl = p.logo;
              if (!logoUrl) {
                if (p.slug === "mycitydekho" || p.title.toLowerCase().includes("citydekho")) {
                  logoUrl = "/brand/citydekho-pin.png";
                } else if (p.slug === "ecopro-builders" || p.title.toLowerCase().includes("ecopro")) {
                  logoUrl = "/brand/ecopro-logo.png";
                } else if (p.slug === "vinnskolam" || p.domain?.includes("vinnskolam")) {
                  logoUrl = "/brand/vinnskolam-logo.png";
                } else if (p.slug === "dewstream" || p.domain?.includes("dewstream")) {
                  logoUrl = "/brand/dewstream-logo.png";
                } else if (p.slug === "paineasy" || p.domain?.includes("paineasy")) {
                  logoUrl = "/brand/paineasy-logo.svg";
                } else if (p.slug === "medylife" || p.slug === "meadylife" || p.domain?.includes("medylife")) {
                  logoUrl = "/brand/medylife-logo.png";
                } else if (p.slug === "latajcollection" || p.domain?.includes("latajcollection")) {
                  logoUrl = "/brand/latajcollection-logo.png";
                } else if (p.domain) {
                  logoUrl = `https://www.google.com/s2/favicons?domain=${p.domain}&sz=128`;
                }
              }
              const displayTitle = (p.slug === "meadylife" || p.slug === "medylife")
                ? "MedyLife"
                : (p.slug === "latajcollection" || p.domain?.includes("latajcollection"))
                  ? "latajcollection"
                  : p.title;
              const sectorLabel = SECTOR_LABELS[p.sector] || (p.sector ? p.sector.replace(/-/g, " ") : p.cat);
              return (
                <Reveal key={p.id || p.slug || i} delay={(i % 3) * 0.08}>
                  <Spotlight className="gborder glass-card rounded-3xl p-5 transition-all duration-300 hover:-translate-y-2 flex flex-col h-full justify-between">
                    <div>
                      <Link to={`/portfolio/${p.slug}`} className="block relative overflow-hidden rounded-2xl group/img">
                        <BrowserMock src={p.img} alt={displayTitle} className="aspect-[16/10] group-hover/img:scale-[1.02] transition-transform duration-500" />
                        {isHome && (
                          <span className="absolute top-3 right-3 text-xs font-semibold px-3 py-1 rounded-full bg-[#FF5A1F] text-white shadow-md">
                            {sectorLabel}
                          </span>
                        )}
                      </Link>
                      <div className="px-2 pt-5 pb-1">
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <Link to={`/portfolio/${p.slug}`} className="flex items-center gap-2.5 group/title">
                            {logoUrl && (
                              <img
                                src={logoUrl}
                                alt={displayTitle}
                                className="w-6 h-6 rounded-md object-contain shrink-0 border border-black/10 bg-white"
                                onError={(e) => {
                                  if (p.slug === "mycitydekho" || p.title.toLowerCase().includes("citydekho")) {
                                    e.target.onerror = null;
                                    e.target.src = "/brand/citydekho-pin.png";
                                  } else if (p.slug === "ecopro-builders" || p.title.toLowerCase().includes("ecopro")) {
                                    e.target.onerror = null;
                                    e.target.src = "/brand/ecopro-logo.png";
                                  } else if (p.slug === "vinnskolam" || p.domain?.includes("vinnskolam")) {
                                    e.target.onerror = null;
                                    e.target.src = "/brand/vinnskolam-logo.png";
                                  } else if (p.slug === "dewstream" || p.domain?.includes("dewstream")) {
                                    e.target.onerror = null;
                                    e.target.src = "/brand/dewstream-logo.png";
                                  } else if (p.slug === "paineasy" || p.domain?.includes("paineasy")) {
                                    e.target.onerror = null;
                                    e.target.src = "/brand/paineasy-logo.svg";
                                  } else if (p.slug === "medylife" || p.slug === "meadylife" || p.domain?.includes("medylife")) {
                                    e.target.onerror = null;
                                    e.target.src = "/brand/medylife-logo.png";
                                  } else if (p.slug === "launchpad" || p.domain?.includes("launchpad")) {
                                    e.target.onerror = null;
                                    e.target.src = "/brand/launchpad-clean.png";
                                  } else if (p.slug === "php2shell" || p.domain?.includes("php2shell")) {
                                    e.target.onerror = null;
                                    e.target.src = "/brand/php2shell-logo.png";
                                  } else if (p.slug === "latajcollection" || p.domain?.includes("latajcollection")) {
                                    e.target.onerror = null;
                                    e.target.src = "/brand/latajcollection-logo.png";
                                  } else if (p.slug === "wordpress-security-hardening" || p.domain?.includes("wordpress-security")) {
                                    e.target.onerror = null;
                                    e.target.src = "/brand/wordpress-security-logo.png";
                                  } else {
                                    e.target.style.display = "none";
                                  }
                                }}
                              />
                            )}
                            <h3 className="font-heading text-xl font-bold text-[#0B0B0B] group-hover/title:text-[#FF5A1F] transition-colors">
                              {displayTitle}
                            </h3>
                          </Link>
                        </div>

                        <p className="text-neutral-500 text-sm leading-relaxed mb-3 sm:mb-4 line-clamp-2">
                          {p.desc}
                        </p>
                      </div>
                    </div>

                    <div className="px-2 pt-3 border-t border-black/5 flex items-center justify-center mt-auto">
                      <Link
                        to={`/portfolio/${p.slug}`}
                        className="relative z-20 inline-flex items-center justify-center gap-1.5 text-[#FF5A1F] font-semibold text-xs sm:text-sm hover:underline cursor-pointer group/cs"
                      >
                        <span>Case Study</span>
                        <ArrowUpRight className="w-3.5 h-3.5 group-hover/cs:translate-x-0.5 group-hover/cs:-translate-y-0.5 transition-transform" />
                      </Link>
                    </div>
                  </Spotlight>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};



/* Case study — 3-card grid preview */
export const CaseStudiesPreview = () => {
  const [cases, setCases] = useState(PROJECTS);

  useEffect(() => {
    api.get("/projects")
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) setCases(res.data);
      })
      .catch(() => {});
  }, []);

  const featuredSlugs = ["indusdeal", "foxrun", "pawsup"];
  const featuredCases = featuredSlugs
    .map((slug) => {
      const p = cases.find((x) => x.slug === slug) || PROJECTS.find((x) => x.slug === slug);
      if (!p) return null;
      const fallback = PROJECTS.find((x) => x.slug === p.slug) || {};
      const safeImg = (p.img && !p.img.includes("lh3.googleusercontent.com")) ? p.img : (fallback.img || (p.slug === "indusdeal" ? "/brand/indusdeal-live-2026.png" : ""));
      return {
        ...fallback,
        ...p,
        img: safeImg,
        challenge: p.challenge || fallback.challenge || "Opaque industrial pricing and lengthy offline procurement cycles.",
        solution: p.solution || fallback.solution || "Built automated RFQ workflows, catalog search, and bidding systems."
      };
    })
    .filter(Boolean);

  return (
    <section className="py-10 sm:py-16 px-5 sm:px-8 mesh-dark relative overflow-hidden" id="case-studies">
      <div className="max-w-7xl mx-auto relative z-10">
        <SectionHead
          dark
          eyebrow="WHAT EMPOWERS US"
          title={<>Envisioning Success with Top-notch <span className="text-gradient">Tech Stack</span></>}
          sub="We pick the right tool for the job and master it with production excellence."
        />

        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {featuredCases.map((p, i) => (
            <Reveal key={p.slug || i} delay={i * 0.1}>
              <div className="glass-card-dark rounded-3xl p-6 flex flex-col justify-between h-full border border-white/10 hover:border-[#FF5A1F]/50 transition-all duration-300 group">
                <div>
                  <div className="relative overflow-hidden rounded-2xl mb-5 aspect-[16/10]">
                    <img
                      src={p.img || (p.slug === "indusdeal" ? "/brand/indusdeal-live-2026.png" : "")}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.onerror = null;
                        if (p.slug === "indusdeal") {
                          e.target.src = "/brand/indusdeal-live-2026.png";
                        } else if (p.slug === "mycitydekho") {
                          e.target.src = "/brand/mycitydekho-preview.png";
                        } else if (p.slug === "ecopro-builders") {
                          e.target.src = "/brand/ecopro-preview.png";
                        }
                      }}
                    />
                    <span className="absolute top-3 right-3 text-xs font-semibold px-3 py-1 rounded-full bg-[#FF5A1F] text-white shadow-md">
                      {SECTOR_LABELS[p.sector] || (p.sector ? p.sector.replace(/-/g, " ") : p.cat)}
                    </span>
                  </div>

                  <h3 className="font-heading text-2xl font-bold text-white mb-2">{p.title}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed mb-4 line-clamp-2">{p.desc}</p>

                  <div className="space-y-2.5 pt-4 border-t border-white/10 text-xs">
                    <div>
                      <span className="text-[#FF5A1F] font-bold uppercase tracking-wider block mb-0.5">Challenge</span>
                      <p className="text-neutral-300 line-clamp-2">{p.challenge}</p>
                    </div>
                    <div>
                      <span className="text-emerald-400 font-bold uppercase tracking-wider block mb-0.5">Solution</span>
                      <p className="text-neutral-300 line-clamp-2">{p.solution}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                  <div className="flex flex-wrap gap-1">
                    {(p.tech || []).slice(0, 2).map((t) => (
                      <span key={t} className="text-[11px] px-2.5 py-0.5 rounded-full bg-white/10 text-neutral-300 font-mono">
                        {t}
                      </span>
                    ))}
                  </div>
                  <Link to={`/portfolio/${p.slug}`} className="inline-flex items-center gap-1 text-[#FF5A1F] text-xs font-bold uppercase tracking-wider group-hover:translate-x-1 transition-transform">
                    View Case Study <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};


/* Process — glass on dark / white bg on selected service pages */
export const ProcessSteps = ({ isHomePage, whiteBg, eyebrow, hideEyebrow = false }) => {
  const location = useLocation();
  const WHITE_BG_SERVICE_PATHS = [
    "/services/software-development",
    "/services/ai-solutions",
    "/services/iot-solutions",
    "/services/cloud-infrastructure",
    "/services/consultancy",
  ];
  const isServicesPage = location.pathname.startsWith("/services");
  const isWhite = Boolean(whiteBg || WHITE_BG_SERVICE_PATHS.includes(location.pathname));
  const isHome = !isWhite && (isHomePage ?? (location.pathname === "/" || location.pathname === ""));
  const actualEyebrow = (hideEyebrow || isHome) ? null : (eyebrow !== undefined ? eyebrow : (isServicesPage ? null : "HOW DO WE OPERATE"));
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: false, amount: 0.15 });

  const [litCount, setLitCount] = useState(0);
  const [lineStep, setLineStep] = useState(0);
  const [activeCircle, setActiveCircle] = useState(-1);
  const timeoutsRef = useRef([]);

  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const runSequence = useCallback(() => {
    clearAllTimeouts();

    // Reset: all circles dark, line at start
    setLitCount(0);
    setLineStep(0);
    setActiveCircle(-1);

    // Initial beat: after 200ms, Circle 01 turns orange glow
    const t0 = setTimeout(() => {
      setLitCount(1);
      setActiveCircle(0);
    }, 200);
    timeoutsRef.current.push(t0);

    const TRAVEL_TIME = 650; // duration for line to travel between adjacent circles
    const PAUSE_TIME = 250;  // pause after circle turns orange before next motion
    const STEP_TOTAL = TRAVEL_TIME + PAUSE_TIME; // 900ms

    // Steps 1 to 7 (Circles 02 to 08):
    for (let i = 1; i < 8; i++) {
      const startMove = 200 + PAUSE_TIME + (i - 1) * STEP_TOTAL;
      const reachCircle = startMove + TRAVEL_TIME;

      // Motion line starts moving towards circle i (circle i is still dark)
      const tMove = setTimeout(() => {
        setLineStep(i);
      }, startMove);
      timeoutsRef.current.push(tMove);

      // Line REACHES circle i: circle i instantly turns orange glow!
      const tReach = setTimeout(() => {
        setLitCount(i + 1);
        setActiveCircle(i);
      }, reachCircle);
      timeoutsRef.current.push(tReach);
    }

    // Once all 8 are reached (at ~6500ms): hold for 2600ms, then restart in continuous loop
    const totalCycle = 200 + PAUSE_TIME + 6 * STEP_TOTAL + TRAVEL_TIME + 2600;
    const tLoop = setTimeout(() => {
      runSequence();
    }, totalCycle);
    timeoutsRef.current.push(tLoop);
  }, [clearAllTimeouts]);

  // Whenever section appears in viewport, run sequence
  useEffect(() => {
    if (isInView) {
      runSequence();
    } else {
      clearAllTimeouts();
    }
    return () => clearAllTimeouts();
  }, [isInView, runSequence, clearAllTimeouts]);

  // Whenever user hovers over this section, restart motion line from start to end
  const lastHoverRef = useRef(0);
  const handleSectionMouseEnter = () => {
    const now = Date.now();
    if (now - lastHoverRef.current > 1800) {
      lastHoverRef.current = now;
      runSequence();
    }
  };

  const handleCircleClick = (i) => {
    clearAllTimeouts();
    setLineStep(i);
    setLitCount(i + 1);
    setActiveCircle(i);
  };

  const lineWidthPercent = (lineStep / 7) * 87.5;

  return (
    <section
      ref={containerRef}
      onMouseEnter={handleSectionMouseEnter}
      className={`py-7 sm:py-11 px-5 sm:px-8 relative overflow-hidden ${
        isWhite ? "bg-white border-y border-black/[0.06]" : isHome ? "bg-[#0B0B0B]" : "mesh-dark"
      }`}
      id="process"
    >
      {/* Background Image on Home page only */}
      {isHome && (
        <div className="absolute inset-0 z-0">
          <img
            src="/brand/process-bg.png"
            alt="NeuraVolt Momentum Collaborative Workspace"
            className="w-full h-full object-cover object-center filter brightness-95"
          />
          {/* Directional gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/65 to-black/85" />
          <div className="absolute inset-0 bg-black/25" />
        </div>
      )}

      <div className="max-w-7xl mx-auto relative z-10">
        <SectionHead
          dark={!isWhite}
          className="mb-5 sm:mb-6"
          eyebrow={actualEyebrow}
          title={<>An Ecosystem Built for <span className="text-gradient">Momentum</span></>}
          sub="We’ve created an elaborative eight step process to deliver a lifetime of partnership."
        />

        {/* Desktop — horizontal stepper with moving motion line and sequential orange glow */}
        <div className="hidden lg:block relative mt-5 sm:mt-7">
          {/* background connector line track */}
          <div className={`absolute top-[28px] left-[6.25%] right-[6.25%] h-[2.5px] ${isWhite ? "bg-black/10" : "bg-white/10"} rounded-full z-0`} />

          {/* glowing motion line traveling horizontally across the steps */}
          <motion.div
            className="absolute top-[27.5px] left-[6.25%] h-[3px] bg-gradient-to-r from-[#FF5A1F] via-[#FF7A00] to-[#FF5A1F] rounded-full z-0 pointer-events-none"
            style={{
              boxShadow: "0 0 16px #FF5A1F, 0 0 32px rgba(255,90,31,0.8)",
            }}
            initial={{ width: "0%" }}
            animate={{
              width: isInView ? `${lineWidthPercent}%` : "0%",
            }}
            transition={{
              duration: lineStep === 0 ? 0.2 : 0.65,
              ease: "easeInOut",
            }}
          >
            {/* White-hot glowing leading head particle */}
            {litCount > 0 && (
              <div
                className="absolute right-[-4px] top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white z-10"
                style={{
                  boxShadow: "0 0 10px #FFFFFF, 0 0 20px #FF5A1F, 0 0 35px #FF5A1F",
                }}
              />
            )}
          </motion.div>

          <div className="grid grid-cols-8 gap-2">
            {PROCESS.map((p, i) => {
              const isLit = i < litCount;
              const isActive = i === activeCircle;

              return (
                <div
                  key={p.n}
                  data-testid={`process-${i}`}
                  className="group flex flex-col items-center text-center cursor-pointer select-none"
                  onClick={() => handleCircleClick(i)}
                >
                  {/* circle step with halo and radiant orange glow */}
                  <div className="relative mb-5 z-10">
                    {isActive && (
                      <span
                        className="absolute -inset-2 rounded-full bg-[#FF5A1F] opacity-70 animate-ping pointer-events-none"
                        style={{ animationDuration: "1.2s" }}
                      />
                    )}
                    <div
                      className={`relative w-14 h-14 rounded-full border-2 grid place-items-center transition-all duration-500 font-mono font-bold text-sm shadow-sm ${
                        isLit
                          ? "bg-[#FF5A1F] border-[#FF5A1F] text-white"
                          : isWhite
                          ? "bg-[#FAF9F6] border-black/15 text-[#0B0B0B] group-hover:border-[#FF5A1F] group-hover:bg-[#FF5A1F] group-hover:text-white"
                          : "bg-[#141414] border-white/15 text-white/50 group-hover:border-[#FF5A1F] group-hover:bg-[#FF5A1F] group-hover:text-white"
                      } ${isActive ? "scale-110 ring-4 ring-[#FF5A1F]/40" : "scale-100"}`}
                      style={{
                        boxShadow: isActive
                          ? "0 0 25px #FF5A1F, 0 0 50px rgba(255,90,31,0.85), 0 0 80px rgba(255,90,31,0.4)"
                          : isLit
                          ? "0 0 15px rgba(255,90,31,0.65), 0 0 30px rgba(255,90,31,0.3)"
                          : "none",
                      }}
                    >
                      {p.n}
                    </div>
                  </div>

                  <h3
                    className={`font-heading text-sm font-bold leading-snug transition-colors duration-300 ${
                      isActive
                        ? "text-[#FF5A1F] scale-105"
                        : isLit
                        ? isWhite ? "text-[#0B0B0B]" : "text-white"
                        : isWhite ? "text-neutral-600" : "text-white/40"
                    }`}
                  >
                    {p.title}
                  </h3>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile — synchronized automotion cards with orange glow */}
        <div className="lg:hidden grid sm:grid-cols-2 gap-4 mt-8">
          {PROCESS.map((p, i) => {
            const isLit = i < litCount;
            const isActive = i === activeCircle;

            return (
              <Reveal key={p.n} delay={(i % 2) * 0.07}>
                <Spotlight
                  className={`border rounded-2xl p-4 sm:p-5 h-full flex gap-4 items-center transition-all duration-300 cursor-pointer ${
                    isActive
                      ? "border-[#FF5A1F] shadow-[0_0_25px_rgba(255,90,31,0.4)] ring-1 ring-[#FF5A1F]"
                      : isLit
                      ? "border-[#FF5A1F]/50 shadow-[0_0_12px_rgba(255,90,31,0.2)]"
                      : isWhite
                      ? "bg-[#FAF9F6] border-black/10 shadow-sm"
                      : "glass-card-dark border-white/10"
                  } ${isWhite ? "bg-[#FAF9F6]" : "glass-card-dark"}`}
                  data-testid={`process-mobile-${i}`}
                  onClick={() => handleCircleClick(i)}
                >
                  <span
                    className={`w-9 h-9 rounded-full font-mono font-bold text-xs grid place-items-center shrink-0 transition-all duration-300 ${
                      isLit
                        ? "bg-[#FF5A1F] text-white shadow-[0_0_15px_#FF5A1F]"
                        : "bg-white/10 text-white/70"
                    }`}
                  >
                    {p.n}
                  </span>
                  <div>
                    <h3
                      className={`font-heading text-sm font-bold transition-colors duration-300 ${
                        isActive ? "text-[#FF5A1F]" : isWhite ? "text-[#0B0B0B]" : "text-white"
                      }`}
                    >
                      {p.title}
                    </h3>
                  </div>
                </Spotlight>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* Technologies — glass marquee on dark with official vector logos */
export const TechMarquee = ({ eyebrow }) => {
  const location = useLocation();
  const isServicesPage = location.pathname.startsWith("/services");
  const actualEyebrow = eyebrow !== undefined ? eyebrow : (isServicesPage ? null : "WHAT EMPOWERS US");
  const strip = [...TECHNOLOGIES, ...TECHNOLOGIES];
  return (
    <section className="py-8 sm:py-12 mesh-dark relative overflow-hidden" id="technologies">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-6 sm:mb-8">
        <SectionHead className="mb-0" dark eyebrow={actualEyebrow} title={<>Envisioning Success with Top-notch <span className="text-gradient">Tech Stack</span></>} sub="We pick the right tool for the job and master it with production excellence." />
      </div>

      <div className="overflow-hidden py-2">
        <div className="flex gap-4 w-max marquee-slow">
          {strip.map((t, i) => {
            const item = typeof t === "string" ? { name: t, logo: "" } : t;
            return (
              <div key={i} className="glass-card-dark rounded-2xl px-6 py-4 flex items-center gap-3 font-heading text-base font-semibold text-neutral-200 whitespace-nowrap border border-white/10 hover:border-[#FF5A1F]/50 transition-all group">
                {item.logo && (
                  <img
                    src={item.logo}
                    alt={item.name}
                    className="w-6 h-6 object-contain shrink-0 group-hover:scale-110 transition-transform duration-300"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                )}
                <span>{item.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* Why choose — custom brand-tailored vector icons matching reference */
const WHY_ICONS = [
  // 1. AI First Approach: Document with folded corner, lines, and 4-point sparkle star
  ({ className = "w-5 h-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h7" />
      <path d="M14.5 2v4.5a2 2 0 0 0 2 2H20v3.5" />
      <line x1="7" y1="11" x2="12" y2="11" />
      <line x1="7" y1="15" x2="10.5" y2="15" />
      <path d="M18 13 C18 15.5 19 16.5 21.5 16.5 C19 16.5 18 17.5 18 20 C18 17.5 17 16.5 14.5 16.5 C17 16.5 18 15.5 18 13 Z" fill="currentColor" fillOpacity="0.15" />
    </svg>
  ),
  // 2. Fast Delivery: Arrowhead with 3 horizontal speed lines
  ({ className = "w-5 h-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <line x1="2.5" y1="7.5" x2="7" y2="7.5" />
      <line x1="1" y1="12" x2="8.5" y2="12" />
      <line x1="2.5" y1="16.5" x2="7" y2="16.5" />
      <path d="M10.5 4.5 L21.5 12 L10.5 19.5 L13 12 Z" />
    </svg>
  ),
  // 3. Scalable Architecture: 3 ascending bar chart columns
  ({ className = "w-5 h-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect x="3" y="13.5" width="4.8" height="7.5" rx="1.5" />
      <rect x="9.6" y="8" width="4.8" height="13" rx="1.5" />
      <rect x="16.2" y="3.5" width="4.8" height="17.5" rx="1.5" />
    </svg>
  ),
  // 4. Transparent Communication: Dual chat speech bubbles
  ({ className = "w-5 h-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M9.5 3.5 C5.6 3.5 2.5 6.4 2.5 10 C2.5 11.5 3.1 12.9 4.2 13.9 L3 17 L6.6 15.8 C7.5 16.2 8.5 16.5 9.5 16.5 C13.4 16.5 16.5 13.6 16.5 10 C16.5 6.4 13.4 3.5 9.5 3.5 Z" />
      <line x1="6.5" y1="10" x2="11.5" y2="10" />
      <path d="M15.5 10.5 C18.8 11.3 21 13.2 21 15.5 C21 16.7 20.3 17.8 19.3 18.6 L20 21 L17.2 20.1 C16.4 20.4 15.5 20.5 14.5 20.5 C12.2 20.5 10.3 19.3 9.5 17.5" />
      <line x1="13.5" y1="16" x2="16.5" y2="16" />
    </svg>
  ),
  // 5. Modern Tech Stack: 3-tier server chassis stack
  ({ className = "w-5 h-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect x="3" y="3.5" width="18" height="4.8" rx="1.5" />
      <rect x="3" y="9.6" width="18" height="4.8" rx="1.5" />
      <rect x="3" y="15.7" width="18" height="4.8" rx="1.5" />
      <line x1="6.5" y1="5.9" x2="13.5" y2="5.9" />
      <line x1="6.5" y1="12" x2="13.5" y2="12" />
      <line x1="6.5" y1="18.1" x2="13.5" y2="18.1" />
      <circle cx="17.2" cy="5.9" r="0.75" fill="currentColor" />
      <circle cx="17.2" cy="12" r="0.75" fill="currentColor" />
      <circle cx="17.2" cy="18.1" r="0.75" fill="currentColor" />
    </svg>
  ),
  // 6. Dedicated Support: Team of 3 persons
  ({ className = "w-5 h-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="12" cy="7.5" r="3.2" />
      <path d="M6.5 20 C6.5 16 9 14.5 12 14.5 C15 14.5 17.5 16 17.5 20" />
      <circle cx="6.5" cy="9.5" r="2.2" />
      <path d="M2.5 19.5 C2.5 17 4.2 15.8 6 15.5" />
      <circle cx="17.5" cy="9.5" r="2.2" />
      <path d="M18 15.5 C19.8 15.8 21.5 17 21.5 19.5" />
    </svg>
  ),
  // 7. Global Standards: Grid globe with equator and latitude/meridian curves
  ({ className = "w-5 h-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="12" cy="12" r="9" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <ellipse cx="12" cy="12" rx="4.5" ry="9" />
      <path d="M4.5 7.5 C7 9.2 17 9.2 19.5 7.5" />
      <path d="M4.5 16.5 C7 14.8 17 14.8 19.5 16.5" />
    </svg>
  ),
  // 8. Design Excellence: Vector pen tool
  ({ className = "w-5 h-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <g transform="rotate(-40 12 12)">
        <path d="M12 2 L19 9 L15.5 16 L12 22 L8.5 16 L5 9 Z" />
        <circle cx="12" cy="10.5" r="1.8" />
        <line x1="12" y1="12.3" x2="12" y2="22" />
      </g>
    </svg>
  ),
];

export const WhyChoose = ({ hideEyebrow = true }) => (
  <section className="py-7 sm:py-11 px-5 sm:px-8 mesh-light" id="why">
    <div className="max-w-7xl mx-auto">
      <SectionHead
        eyebrow={hideEyebrow ? null : "WHY CHOOSE US"}
        title={<>Helping You Develop What You <span className="text-gradient">Need</span></>}
        sub="Reasons why ambitious teams choose NeuraVolt."
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
        {WHY.map((w, i) => {
          const WhyIcon = WHY_ICONS[i] || WHY_ICONS[0];
          return (
            <Reveal key={w.title} delay={(i % 4) * 0.07}>
              <Spotlight className="group gborder glass-card rounded-2xl sm:rounded-3xl p-4 sm:p-7 h-full transition-all duration-300 hover:-translate-y-1.5" data-testid={`why-${i}`}>
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl sm:rounded-2xl bg-[#FF5A1F]/10 border border-[#FF5A1F]/20 flex items-center justify-center text-[#FF5A1F] mb-3 sm:mb-4 group-hover:bg-[#FF5A1F] group-hover:text-white transition-all duration-300">
                  <WhyIcon className="w-5 h-5 sm:w-5.5 sm:h-5.5" />
                </div>
                <h3 className="font-heading text-sm sm:text-lg font-semibold mb-1.5 sm:mb-2 text-[#0B0B0B]">{w.title}</h3>
                <p className="text-neutral-500 text-xs sm:text-sm leading-relaxed">{w.desc}</p>
                <div className="mt-3 sm:mt-4 h-0.5 rounded-full w-8 transition-all duration-500 group-hover:w-full bg-gradient-to-r from-[#FF5A1F] to-transparent" />
              </Spotlight>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);


/* Client website screenshot mapping for Testimonials dynamic animated background */
const CLIENT_TESTIMONIAL_BG_IMAGES = {
  "foxrun": "/brand/foxrun-preview.png",
  "vinnskolam": "/brand/vinnskolam-preview.png",
  "paineasy": "/brand/paineasy-preview.png",
  "indusdeal": "/brand/indusdeal-preview.png",
  "clueblu": "/brand/clueblu-preview.png",
  "clueble": "/brand/clueblu-preview.png",
  "ecoprobuilders": "/brand/ecopro-preview.png",
  "ecopro-builders": "/brand/ecopro-preview.png",
  "ecopro builders": "/brand/ecopro-preview.png",
  "buildhealer": "/brand/buildhealer-preview.png",
  "krishna saree": "/brand/krishnasaree-preview.png",
  "krishnasaree": "/brand/krishnasaree-preview.png",
  "paws up": "/brand/pawsup-preview.png",
  "pawsup": "/brand/pawsup-preview.png",
  "pawsup uae": "/brand/pawsup-preview.png",
  "kolams of india": "/brand/kolamsofindia-preview.png",
  "kolams": "/brand/kolamsofindia-preview.png",
};

/* Client Avatar with circular disc and two thin orange orbiting progress arcs with glowing endpoints */
const ClientAvatar = ({ initial }) => (
  <div className="relative w-28 h-28 sm:w-32 sm:h-32 shrink-0 grid place-items-center select-none">
    {/* Subtle atmospheric ambient glow */}
    <div className="absolute inset-0 rounded-full bg-[#FF5A1F]/20 blur-xl pointer-events-none" />

    {/* Orbiting progress arcs with glowing endpoints */}
    <motion.svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox="0 0 140 140"
      animate={{ rotate: 360 }}
      transition={{ repeat: Infinity, duration: 16, ease: "linear" }}
    >
      <defs>
        <linearGradient id="arcGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF5A1F" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#FF5A1F" stopOpacity="1" />
        </linearGradient>
        <linearGradient id="arcGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF5A1F" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#FF5A1F" stopOpacity="1" />
        </linearGradient>
        <filter id="endpointGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.8" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Faint static background track circle */}
      <circle
        cx="70"
        cy="70"
        r="56"
        fill="none"
        stroke="rgba(255, 255, 255, 0.08)"
        strokeWidth="1.5"
      />

      {/* Arc 1: ~90-deg top-right arc */}
      <path
        d="M 84.5 15.9 A 56 56 0 0 1 124.1 84.5"
        fill="none"
        stroke="url(#arcGrad1)"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* Glowing Endpoint 1 */}
      <circle cx="124.1" cy="84.5" r="5" fill="#FF5A1F" opacity="0.45" filter="url(#endpointGlow)" />
      <circle cx="124.1" cy="84.5" r="2.5" fill="#FF7442" />

      {/* Arc 2: ~90-deg bottom-left arc */}
      <path
        d="M 55.5 124.1 A 56 56 0 0 1 15.9 55.5"
        fill="none"
        stroke="url(#arcGrad2)"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* Glowing Endpoint 2 */}
      <circle cx="15.9" cy="55.5" r="5" fill="#FF5A1F" opacity="0.45" filter="url(#endpointGlow)" />
      <circle cx="15.9" cy="55.5" r="2.5" fill="#FF7442" />
    </motion.svg>

    {/* Center Circular Disc: subtle dark-orange fill with initial */}
    <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-[#451908] via-[#2f1205] to-[#180802] border border-white/10 shadow-[inset_0_2px_8px_rgba(255,255,255,0.08),0_10px_25px_rgba(0,0,0,0.6)] grid place-items-center">
      <div className="absolute inset-1 rounded-full border border-[#FF5A1F]/25 pointer-events-none" />
      <span className="font-heading font-bold text-3xl sm:text-4xl text-white tracking-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
        {initial}
      </span>
    </div>
  </div>
);

/* Testimonials — sleek compact review slider with dynamic company website background */
export const Testimonials = ({ hideEyebrow }) => {
  const [idx, setIdx] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setIdx((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 1700);
    return () => clearInterval(timer);
  }, [isPaused]);

  const t = TESTIMONIALS[idx] || TESTIMONIALS[0];

  const projectMatch = PROJECTS.find(
    (p) =>
      p.title.toLowerCase() === (t.company || "").toLowerCase() ||
      p.slug === (t.company || "").toLowerCase().replace(/\s+/g, "-") ||
      (p.domain && (t.company || "").toLowerCase().includes(p.domain.replace(/\.[a-z]+$/, "")))
  );

  const companyKey = (t.company || "").toLowerCase().trim();
  const activeBgImage = CLIENT_TESTIMONIAL_BG_IMAGES[companyKey] || projectMatch?.img || "/brand/buildhealer-preview.png";
  const caseStudyTitle = projectMatch?.title || t.company || "Project";
  const caseStudyLink = projectMatch ? `/portfolio/${projectMatch.slug}` : "/portfolio";

  return (
    <section className="py-8 sm:py-12 relative overflow-hidden bg-[#0B0B0B]" id="testimonials">
      {/* Animated Company Website Screenshot Background - Full Horizontal Coverage Edge-to-Edge */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden select-none">
        <AnimatePresence mode="sync">
          <motion.div
            key={activeBgImage || idx}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.88 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full"
          >
            {/* Full-width Website Hero Section Screenshot */}
            <img
              src={activeBgImage}
              alt={t.company}
              className="w-full h-full object-cover object-top filter brightness-[0.85] contrast-[1.08] saturate-[1.12]"
            />

            {/* Edge blending gradients to connect seamlessly with sections above & below */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/85 via-[#0B0B0B]/35 to-[#0B0B0B]/90" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B]/60 via-transparent to-[#0B0B0B]/60" />
            <div className="absolute inset-0 bg-black/20" />
            {/* Subtle atmospheric brand glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[450px] bg-[#FF5A1F]/10 rounded-full blur-[120px] pointer-events-none" />
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 relative z-10">
        {/* Main Heading: "What Our Clients Say" */}
        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-center text-white mb-8 sm:mb-10 drop-shadow-[0_2px_24px_rgba(255,90,31,0.25)]"
        >
          What Our <span className="text-[#FF5A1F] drop-shadow-[0_0_25px_rgba(255,90,31,0.45)]">Clients</span> Say
        </motion.h2>

        {/* Testimonial Content Container */}
        <div
          className="relative py-2 sm:py-4 px-2 sm:px-4 flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-8 lg:gap-10 relative z-10"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Left Column: Client Avatar (transitions smoothly when idx changes) */}
          <div className="shrink-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
              >
                <ClientAvatar initial={t.name ? t.name.charAt(0) : "S"} />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Slim Vertical Divider */}
          <div className="hidden sm:block w-[1px] self-stretch bg-gradient-to-b from-transparent via-white/15 to-transparent shrink-0 my-1" />

          {/* Right Column: Rating on top (wipes once on entrance) + Changing Quote & Author below */}
          <div className="flex-1 min-w-0 space-y-3.5 sm:space-y-4 text-center sm:text-left">
            {/* 5 Orange Stars + Muted 5.0 / 5 Rating — Discrete Star-by-Star Sequential Reveal */}
            <div className="flex items-center justify-center sm:justify-start gap-2.5 select-none drop-shadow-[0_2px_10px_rgba(0,0,0,0.85)]">
              {/* Stars Container: 5 discrete stars that illuminate one by one */}
              <div className="flex items-center gap-1.5 py-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="relative w-4 h-4 sm:w-[18px] sm:h-[18px] grid place-items-center">
                    {/* Background Faint Star Silhouette (visible before lighting up) */}
                    <Star
                      className="w-full h-full text-[#FF5A1F]/25 fill-[#FF5A1F]/15 stroke-current"
                      strokeWidth={1.5}
                    />

                    {/* Foreground Vibrant Star: Becomes visible one by one automatically */}
                    <motion.div
                      key={`star-lit-${idx}-${i}`}
                      initial={{ opacity: 0, scale: 0.3 }}
                      animate={{ opacity: 1, scale: [0.3, 1.25, 1] }}
                      transition={{
                        delay: i * 0.09,
                        duration: 0.28,
                        ease: [0.34, 1.56, 0.64, 1],
                      }}
                      className="absolute inset-0 grid place-items-center"
                    >
                      <Star
                        className="w-full h-full text-[#FF5A1F] fill-[#FF5A1F] drop-shadow-[0_0_8px_rgba(255,90,31,0.75)]"
                        strokeWidth={1.5}
                      />
                    </motion.div>
                  </div>
                ))}
              </div>

              {/* Rating Numeric Score with synchronized reveal as 5th star lights up */}
              <motion.div
                key={`score-${idx}`}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.38, duration: 0.25, ease: "easeOut" }}
                className="flex items-baseline gap-1 font-mono text-xs sm:text-sm font-medium tracking-wide ml-0.5"
              >
                <span className="text-white font-bold tracking-tight">5.0</span>
                <span className="text-neutral-400">/ 5</span>
              </motion.div>
            </div>

            {/* Changing Quote, Author & CTA: Smoothly transitions on each testimonial shuffle */}
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="space-y-3.5 sm:space-y-4"
              >
                {/* Quote with Small Orange Quotation Marks */}
                <blockquote
                  data-testid="testimonial"
                  className="font-heading text-base sm:text-lg lg:text-xl font-normal text-white leading-relaxed tracking-normal drop-shadow-[0_2px_12px_rgba(0,0,0,0.95)]"
                >
                  <span className="text-[#FF5A1F] font-serif font-black text-xl sm:text-2xl mr-1 select-none inline-block align-baseline">“</span>
                  {t.quote}
                  <span className="text-[#FF5A1F] font-serif font-black text-xl sm:text-2xl ml-1 select-none inline-block align-baseline">”</span>
                </blockquote>

                {/* Author Details & Case Study CTA */}
                <div className="pt-2 flex flex-wrap items-center justify-center sm:justify-start gap-y-3 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                  <div>
                    <div className="font-heading font-semibold text-white text-sm sm:text-base tracking-tight leading-tight">
                      {t.name}
                    </div>
                    <div className="text-xs sm:text-sm text-neutral-400 font-normal mt-0.5">
                      {t.role}
                    </div>
                  </div>

                  {caseStudyLink && (
                    <>
                      <div className="hidden sm:block w-[1px] h-7 bg-white/15 mx-4 sm:mx-6 shrink-0" />
                      <Link
                        to={caseStudyLink}
                        className="text-[#FF5A1F] hover:text-[#ff7442] hover:underline font-semibold text-xs sm:text-sm inline-flex items-center gap-1.5 transition-colors group"
                      >
                        <span>Explore {caseStudyTitle} Case Study</span>
                        <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </Link>
                    </>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};


/* Leadership — premium founder glass profiles */
export const Leadership = () => {
  const [members, setMembers] = useState(TEAM);

  useEffect(() => {
    api.get("/team")
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) setMembers(res.data);
      })
      .catch(() => {});
  }, []);

  return (
    <section className="py-14 sm:py-28 px-5 sm:px-8 mesh-light" id="team">
      <div className="max-w-7xl mx-auto">
        <SectionHead eyebrow="Leadership" title="The people behind Neuravolt" sub="Founders and a core engineering team obsessed with craft." />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 items-stretch">
          {members.map((m, i) => {
            const displayRole = (m.role || "")
              .replace(/Head of Product Engineering\s*(&\s*)?/gi, "")
              .replace(/HOPE\s*(&\s*)?/gi, "")
              .trim();

            return (
              <Reveal key={m.id || i} delay={(i % 4) * 0.07} className="h-full">
                <div className="group relative editable h-full flex flex-col">
                  <Spotlight className="gborder glass-card rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-7 text-center overflow-hidden flex flex-col items-center justify-between h-full w-full">
                    <div className="blob w-32 h-32 bg-[#FF5A1F] -top-6 left-1/2 -translate-x-1/2 -z-10" style={{ opacity: 0.12 }} />
                    
                    <div className="w-full flex flex-col items-center flex-1">
                      {m.img ? (
                        <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-2xl mx-auto mb-3 sm:mb-4 overflow-hidden border-2 border-[#FF5A1F]/30 shadow-lg shadow-[#FF5A1F]/10 relative bg-gradient-to-br from-[#FF5A1F] to-[#7a2b00] grid place-items-center shrink-0">
                          <img
                            src={m.img}
                            alt={m.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              e.target.onerror = null;
                              if (m.fallbackImg && e.target.src !== m.fallbackImg) {
                                e.target.src = m.fallbackImg;
                              } else {
                                e.target.style.display = "none";
                              }
                            }}
                          />
                          <span className="absolute inset-0 grid place-items-center text-white font-display text-lg sm:text-xl font-bold -z-10">
                            {m.initials || (m.name ? m.name.substring(0, 2).toUpperCase() : "NV")}
                          </span>
                        </div>
                      ) : (
                        <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-2xl mx-auto mb-3 sm:mb-4 grid place-items-center bg-gradient-to-br from-[#FF5A1F] to-[#7a2b00] text-white font-display text-lg sm:text-xl font-bold shrink-0" style={{ boxShadow: "0 12px 30px -10px rgba(255,90,31,0.6)" }}>
                          {m.initials || (m.name ? m.name.substring(0, 2).toUpperCase() : "NV")}
                        </div>
                      )}

                      <h3 className="font-heading text-sm sm:text-base font-semibold text-[#0B0B0B] truncate w-full">{m.name}</h3>
                      <p className="text-[11px] sm:text-xs xl:text-sm text-[#FF5A1F] font-medium mt-1 whitespace-nowrap truncate w-full" title={displayRole}>{displayRole}</p>
                      {m.bio && <p className="text-[11px] sm:text-xs text-neutral-500 mt-2 line-clamp-2 h-8 sm:h-9 leading-relaxed">{m.bio}</p>}
                    </div>

                    {m.linkedin && (
                      <div className="w-full pt-3 mt-auto flex justify-center">
                        <a
                          href={m.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-[#0A66C2] bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20 px-3 py-1 rounded-full font-medium transition-colors"
                          title={`Verify ${m.name} on LinkedIn`}
                        >
                          <Linkedin className="w-3.5 h-3.5" />
                          <span>Verified Profile</span>
                        </a>
                      </div>
                    )}
                  </Spotlight>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* Blogs — editorial magazine layout showing 1 blog from each requested category: Enterprise Strategy, Cloud & AI, AI, Cloud, ERP, HRMS */
const HOME_BLOG_CATEGORIES = [
  "Enterprise Strategy",
  "Cloud & AI",
  "AI",
  "Cloud",
  "ERP",
  "HRMS",
];

export const BlogsPreview = () => {
  const homeBlogs = useMemo(() => {
    const list = Array.isArray(BLOGS) ? BLOGS : [];
    const chosen = [];
    const usedSlugs = new Set();

    HOME_BLOG_CATEGORIES.forEach((cat) => {
      const target = cat.trim().toLowerCase();
      const found = list.find((b) => {
        const c = (b.category || "").trim().toLowerCase();
        return c === target && !usedSlugs.has(b.slug);
      });
      if (found) {
        chosen.push(found);
        usedSlugs.add(found.slug);
      }
    });

    // Fallback if any category wasn't found to guarantee 6 cards
    if (chosen.length < 6) {
      for (const b of list) {
        if (!usedSlugs.has(b.slug)) {
          chosen.push(b);
          usedSlugs.add(b.slug);
          if (chosen.length === 6) break;
        }
      }
    }
    return chosen;
  }, []);

  const [lead, ...rest] = homeBlogs;

  if (!lead) return null;

  return (
    <section className="py-7 sm:py-11 px-5 sm:px-8 mesh-light" id="blog">
      <div className="max-w-7xl mx-auto">
        <SectionHead eyebrow="Insights" title="From the Neuravolt journal" sub="Ideas on AI, engineering and digital transformation." />
        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          <Reveal>
            <Link to={`/blog/${lead.slug}`} className="group flex flex-col justify-between gborder glass-card rounded-3xl overflow-hidden h-full">
              <div>
                <div className="w-full aspect-[2/1] overflow-hidden bg-neutral-100">
                  <img
                    src={lead.img}
                    alt={lead.title}
                    loading="lazy"
                    onError={(e) => {
                      if (lead.img && !e.target.src.includes("neuravolt.in")) {
                        e.target.src = `https://neuravolt.in${lead.img.startsWith("/") ? lead.img : "/" + lead.img}`;
                      }
                    }}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 sm:p-8">
                  <div className="flex items-center gap-2 text-xs text-neutral-400 mb-3"><span className="text-[#FF5A1F] font-semibold">{lead.category}</span> · {lead.read}</div>
                  <h3 className="font-display text-2xl font-bold text-[#0B0B0B] leading-snug mb-2">{lead.title}</h3>
                  <p className="text-neutral-500 line-clamp-3">{lead.excerpt}</p>
                </div>
              </div>
            </Link>
          </Reveal>
          <div className="flex flex-col justify-between space-y-2.5 sm:space-y-3">
            {rest.map((b, i) => (
              <Reveal key={b.slug} delay={i * 0.08}>
                <Link to={`/blog/${b.slug}`} className="group flex flex-col sm:flex-row gap-3 sm:gap-3.5 gborder glass-card rounded-2xl p-2.5 sm:p-3 items-start sm:items-center">
                  <div className="w-full sm:w-36 md:w-40 aspect-[2/1] rounded-xl overflow-hidden shrink-0 bg-neutral-100">
                    <img
                      src={b.img}
                      alt={b.title}
                      loading="lazy"
                      onError={(e) => {
                        if (b.img && !e.target.src.includes("neuravolt.in")) {
                          e.target.src = `https://neuravolt.in${b.img.startsWith("/") ? b.img : "/" + b.img}`;
                        }
                      }}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[#FF5A1F] font-semibold mb-1">{b.category} · {b.read}</div>
                    <h3 className="font-heading font-semibold text-[#0B0B0B] text-sm sm:text-[14px] leading-snug line-clamp-2">{b.title}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="mt-8 sm:mt-10 text-center"><Link to="/blog" className="inline-flex items-center gap-2 text-[#FF5A1F] font-semibold group">Read all articles <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></Link></div>
      </div>
    </section>
  );
};

/* FAQ — glass accordion (2 columns: 3 left, 3 right) */
export const FAQ = ({ items, title = "Questions, answered", eyebrow = "FAQ", sub, isHomePage, isContactPage, whiteBg, hideEyebrow = false }) => {
  const location = useLocation();
  const WHITE_BG_SERVICE_PATHS = [
    "/services/software-development",
    "/services/ai-solutions",
    "/services/iot-solutions",
    "/services/cloud-infrastructure",
    "/services/consultancy",
  ];
  const isWhite = Boolean(whiteBg || isContactPage || location.pathname === "/contact" || WHITE_BG_SERVICE_PATHS.includes(location.pathname));
  const isHome = !isWhite && (isHomePage ?? (location.pathname === "/" || location.pathname === ""));
  const faqList = items && items.length > 0 ? items : FAQS;
  const mid = Math.ceil(faqList.length / 2);
  const leftFaqs = faqList.slice(0, mid);
  const rightFaqs = faqList.slice(mid);

  return (
    <section
      className={`py-7 sm:py-10 px-5 sm:px-8 relative overflow-hidden ${
        isWhite ? "bg-white border-t border-black/[0.06]" : isHome ? "bg-[#0B0B0B]" : "mesh-dark"
      }`}
      id="faq"
    >
      {/* Background Image on Home page only */}
      {isHome && (
        <div className="absolute inset-0 z-0">
          <img
            src="/brand/faq-bg.png"
            alt="NeuraVolt Workspace Environment"
            className="w-full h-full object-cover object-center filter brightness-95"
          />
          {/* Directional gradient overlay: keeps the sunlight, laptop, and office scene visible while providing deep contrast for the FAQ accordions */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/65 to-black/85" />
          <div className="absolute inset-0 bg-black/25" />
        </div>
      )}

      <div className="max-w-6xl mx-auto relative z-10">
        <SectionHead dark={!isWhite} className="mb-6 sm:mb-8" eyebrow={(hideEyebrow || isHome || isContactPage || location.pathname === "/contact") ? null : eyebrow} title={title} sub={sub} center />
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 items-start">
            {/* Left Column */}
            <div className="space-y-3 sm:space-y-4">
              {leftFaqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className={`rounded-2xl px-5 sm:px-6 transition-all duration-300 ${
                    isWhite
                      ? "bg-white/80 backdrop-blur-md border border-black/10 shadow-sm hover:border-[#FF5A1F]/50 hover:shadow-md [&[data-state=open]]:border-[#FF5A1F]/60"
                      : "bg-white/[0.05] hover:bg-white/[0.08] backdrop-blur-xl border border-white/[0.12] hover:border-[#FF5A1F]/50 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),0_10px_30px_-10px_rgba(0,0,0,0.5)] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.22),0_16px_36px_-10px_rgba(0,0,0,0.6),0_0_24px_-4px_rgba(255,90,31,0.25)] [&[data-state=open]]:bg-white/[0.08] [&[data-state=open]]:border-[#FF5A1F]/60 [&[data-state=open]]:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.25),0_20px_40px_-10px_rgba(0,0,0,0.7),0_0_30px_-6px_rgba(255,90,31,0.3)]"
                  }`}
                >
                  <AccordionTrigger
                    data-testid={`faq-${i}`}
                    className={`text-left font-heading font-semibold hover:no-underline py-4 sm:py-5 text-sm sm:text-base transition-colors ${
                      isWhite
                        ? "text-[#0B0B0B] hover:text-[#FF5A1F] [&>svg]:text-neutral-500 hover:[&>svg]:text-[#FF5A1F] [&[data-state=open]]:text-[#FF5A1F] [&[data-state=open]>svg]:text-[#FF5A1F]"
                        : "text-neutral-100 hover:text-white [&>svg]:text-white/60 hover:[&>svg]:text-white [&[data-state=open]]:text-[#FF5A1F] [&[data-state=open]>svg]:text-[#FF5A1F]"
                    }`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent
                    className={`leading-relaxed text-xs sm:text-sm pb-5 ${
                      isWhite
                        ? "text-neutral-600 border-t border-black/5 mt-1 pt-3.5"
                        : "text-neutral-200/90 border-t border-white/[0.08] mt-1 pt-3.5"
                    }`}
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </div>

            {/* Right Column */}
            <div className="space-y-3 sm:space-y-4">
              {rightFaqs.map((f, idx) => {
                const i = idx + leftFaqs.length;
                return (
                  <AccordionItem
                    key={i}
                    value={`item-${i}`}
                    className={`rounded-2xl px-5 sm:px-6 transition-all duration-300 ${
                      isWhite
                        ? "bg-white/80 backdrop-blur-md border border-black/10 shadow-sm hover:border-[#FF5A1F]/50 hover:shadow-md [&[data-state=open]]:border-[#FF5A1F]/60"
                        : "bg-white/[0.05] hover:bg-white/[0.08] backdrop-blur-xl border border-white/[0.12] hover:border-[#FF5A1F]/50 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),0_10px_30px_-10px_rgba(0,0,0,0.5)] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.22),0_16px_36px_-10px_rgba(0,0,0,0.6),0_0_24px_-4px_rgba(255,90,31,0.25)] [&[data-state=open]]:bg-white/[0.08] [&[data-state=open]]:border-[#FF5A1F]/60 [&[data-state=open]]:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.25),0_20px_40px_-10px_rgba(0,0,0,0.7),0_0_30px_-6px_rgba(255,90,31,0.3)]"
                    }`}
                  >
                    <AccordionTrigger
                      data-testid={`faq-${i}`}
                      className={`text-left font-heading font-semibold hover:no-underline py-4 sm:py-5 text-sm sm:text-base transition-colors ${
                        isWhite
                          ? "text-[#0B0B0B] hover:text-[#FF5A1F] [&>svg]:text-neutral-500 hover:[&>svg]:text-[#FF5A1F] [&[data-state=open]]:text-[#FF5A1F] [&[data-state=open]>svg]:text-[#FF5A1F]"
                          : "text-neutral-100 hover:text-white [&>svg]:text-white/60 hover:[&>svg]:text-white [&[data-state=open]]:text-[#FF5A1F] [&[data-state=open]>svg]:text-[#FF5A1F]"
                      }`}
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent
                      className={`leading-relaxed text-xs sm:text-sm pb-5 ${
                        isWhite
                          ? "text-neutral-600 border-t border-black/5 mt-1 pt-3.5"
                          : "text-neutral-200/90 border-t border-white/[0.08] mt-1 pt-3.5"
                      }`}
                    >
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </div>
          </div>
        </Accordion>
      </div>
    </section>
  );
};

/* CTA — cinematic */
export const CTA = () => (
  <section className="py-12 sm:py-24 px-5 sm:px-8 mesh-light">
    <div className="max-w-6xl mx-auto bg-[#0B0B0B] rounded-[2.5rem] px-8 sm:px-16 py-24 text-center relative overflow-hidden border border-white/10 shadow-2xl">
      {/* Background Image: sharp team collaboration meeting photo without blur */}
      <div className="absolute inset-0 z-0">
        <img
          src="/brand/cta-meeting-bg.jpg"
          alt="NeuraVolt Team Collaboration"
          className="w-full h-full object-cover object-center"
        />
        {/* Contrast overlay: keeps team meeting clearly visible while ensuring text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/65 to-black/85" />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      <div className="blob w-[400px] h-[400px] bg-[#FF5A1F] top-0 left-1/3 animate-aurora" style={{ opacity: 0.2 }} />
      <div className="relative z-10">
        <Reveal><h2 className="font-display text-4xl sm:text-6xl font-bold text-white leading-[1.05] text-balance">Let's build your <span className="text-gradient">breakthrough</span></h2></Reveal>
        <Reveal delay={0.15}><p className="mt-6 text-lg text-neutral-300 max-w-xl mx-auto">Book a free consultation and get a plan to move your product forward.</p></Reveal>
        <Reveal delay={0.3}>
          <Magnetic className="mt-10 inline-block">
            <button onClick={() => openConsultationModal()} data-testid="cta-btn" className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#FF5A1F] text-white font-semibold hover:bg-[#ff6d38] transition-colors" style={{ boxShadow: "0 16px 40px -12px rgba(255,90,31,0.6)" }}>
              Book Free Consultation <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </Magnetic>
        </Reveal>

      </div>
    </div>
  </section>
);
